<script>
	// V3 블록 저작 탭. 블록 = 트리거 설명 + contents[](타입드) + buttons[].
	// contents는 서버 blockSchemas(getBlockSchemas)로 폼 자동생성(SchemaForm).
	import { onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { createAsset } from "./imageAssetApi";
	import { buildLintMap, summarizeLint } from "./stepLint";
	import { autoGrow } from "./autoGrow";
	import SchemaForm from "./SchemaForm.svelte";
	import BlockContentsEditor from "./BlockContentsEditor.svelte";
	import ImagePicker from "./ImagePicker.svelte";
	import { listImageAssets, chatAssetFolder } from "./imageAssetApi";
	import { iframeRefStore } from "../store/iframe-store.js";
	import { Sheet, Badge, Button, Select } from "../../lib/ui";
	import { NICKNAME_KEY, knownValues } from "./learnerDecls";
	import ReplyStylePicker from "./ReplyStylePicker.svelte";
	import { replySummary } from "./replyStyles";
	import { SCENE_TYPES, sceneOf } from "./sceneTypes";

	export let chat;
	const tab = "block";

	// 이미지 자산 맵(assetId→자산). 블록 콘텐츠는 assetId(ref)만 저장하고, 미리보기 URL은 여기서 resolve.
	// → 교체/롤백으로 current 버전이 바뀌면 이 맵을 새로고침해 미리보기도 최신으로 반영.
	let assetMap = {};
	const loadAssetMap = async () => {
		if (!chat?.chatId) return;
		try {
			const list = await listImageAssets(chat.chatId);
			const m = {};
			for (const a of list) m[a.id] = a;
			assetMap = m;
		} catch (e) {
			/* 목록 로드 실패는 콘텐츠에 캐시된 url로 폴백 */
		}
	};
	// assetId 우선 resolve, 없으면 콘텐츠에 캐시된 url(레거시/씬이미지 하위호환)
	const assetUrl = (assetId, cacheUrl) => (assetId && assetMap[assetId]?.url) || cacheUrl || "";

	// ── 프리뷰 iframe(런타임)으로 블록 단독 미리보기 전송 ──
	// 편집 중인 블록에 포커스가 들어오면 대화 대신 그 블록 렌더를 보여주고, 빠지면 대화로 복귀.
	// 런타임(CHAT)은 useBlockPreview가 이 메시지를 받아 처리한다. (types/chat.ts 상수와 동일 문자열)
	const BLOCK_PREVIEW_MSG = "AVATALK_BLOCK_PREVIEW";
	const BLOCK_PREVIEW_CLEAR_MSG = "AVATALK_BLOCK_PREVIEW_CLEAR";
	let activePreviewBlock = null; // 현재 프리뷰 중인 블록(객체 동일성으로 판정 — 신규 블록 id=null 대비)

	// 빌더 콘텐츠 1개 → 런타임 렌더용 콘텐츠. assetId는 url로 resolve, AI 텍스트는 자리표시로.
	const previewContent = (c) => {
		const d = c.data || {};
		if (c.type === "text") {
			// mode 미설정=하위호환 ai. static이면 저작 문구 그대로, ai면 자리표시(런타임에 LLM이 채움).
			if ((d.mode ?? "ai") !== "static")
				return { type: "text", data: { placeholder: true, hint: (d.prompt || "").trim() } };
			return { type: "text", data: { text: d.text || "" } };
		}
		if (c.type === "image")
			return { type: "image", data: { url: assetUrl(d.assetId, d.url), alt: d.alt || "", ...(d.variant ? { variant: d.variant } : {}) } };
		if (c.type === "gallery")
			return {
				type: "gallery",
				data: {
					variant: d.variant || "single",
					images: (d.images || [])
						.map((im) => ({ url: assetUrl(im.assetId, im.url), alt: im.alt || "" }))
						.filter((im) => im.url),
				},
			};
		if (c.type === "card")
			return {
				type: "card",
				data: {
					title: d.title || "",
					body: d.body || "",
					imageUrl: assetUrl(d.assetId, d.imageUrl),
					url: (d.url || "").trim(),
				},
			};
		if (c.type === "map")
			return {
				type: "map",
				data: { ...d, imageUrl: assetUrl(d.assetId, d.imageUrl) },
			};
		return { type: c.type, data: { ...d } };
	};

	const buildPreviewBlock = (block) => ({
		id: block.id ?? null,
		terminal: !!block.terminal,
		contents: (block.contents || []).map(previewContent),
		buttons: (block.buttons || []).map((b) =>
			b.type === "navigate"
				? { type: "navigate", label: b.label || "", targetBlockId: b.targetBlockId || "" }
				: { type: "followup", label: b.label || "", text: b.text || "" },
		),
	});

	const postToIframe = (msg) => {
		const iframe = iframeRefStore.get();
		const win = iframe?.contentWindow;
		// [debug] 프리뷰 송신 로그 — 포커스/편집 시 이게 안 찍히면 focusin 미발화 또는 iframe 미등록.
		console.log("[block-preview:send]", msg.type, {
			hasIframe: !!iframe,
			hasWin: !!win,
			targetOrigin: window.location.origin,
			iframeSrc: iframe?.getAttribute?.("src"),
			contents: msg.block?.contents?.length,
		});
		try {
			if (win) win.postMessage(msg, window.location.origin);
		} catch (e) {
			console.warn("[block-preview:send] postMessage 실패", e);
		}
	};
	const postPreview = (block) => {
		activePreviewBlock = block;
		postToIframe({ type: BLOCK_PREVIEW_MSG, block: buildPreviewBlock(block) });
		// 폼 블록(저장됨)을 포커스하면 미리보기 패널 '응답' 탭의 조회 대상으로 지정한다.
		// 포커스아웃돼도 유지(sticky) → 패널의 응답 탭을 클릭해도 대상이 사라지지 않음.
		if (block.id && (block.contents || []).some((c) => c.type === "form")) {
			chat.responsesBlockId = block.id;
			chat.responsesBlockLabel = (block.description || "").split("\n")[0].trim() || block.id;
			chat.assign();
		}
	};
	const clearPreview = () => {
		activePreviewBlock = null;
		postToIframe({ type: BLOCK_PREVIEW_CLEAR_MSG });
	};

	let blocks = [];
	let schemas = {}; // { text:{type,label,fields}, image:..., gallery:..., card:... }
	$: schemaTypes = Object.values(schemas);

	// 단건 "이 말에 어떤 블록?" 테스트는 오른쪽 프리뷰 '테스트 모드'(답변마다 배지)로 이관.
	// 여기 Sheet는 '예상 질문 일괄 점검'(아래 자동 프로브)만 담당.
	let showTestSheet = false;

	// ── 자동 프로브 테스트: LLM이 블록별 발화 + off-topic 유인 발화를 생성해 한꺼번에 라우터에 돌림.
	//    ✅정상보다 ⚠️충돌(collision)·⚠️누수(leak)가 실제로 봐야 할 신호(스모크 테스트). ──
	let probeLoading = false;
	let probeReport = null; // { probes:[{outcome,...}], summary, catalogCount, minConfidence }
	const outcomeIcon = (o) =>
		({ hit: "✅", collision: "⚠️", miss: "⚪", leak: "⚠️", ok_open: "✅" })[o] || "•";
	// 일상어 라벨(개발자말 금지): 저작자가 바로 이해하게
	const outcomeLabel = (o) =>
		({
			hit: "잘 나와요",
			collision: "엉뚱한 블록",
			miss: "안 나와요",
			leak: "상관없는데 나옴",
			ok_open: "자유 답변",
		})[o] || o;
	// Badge variant: 문제(엉뚱/상관없는데 나옴)=경고(required), 안나옴=muted(optional), 정상=secondary
	const badgeVariant = (o) =>
		o === "collision" || o === "leak" ? "required" : o === "miss" ? "optional" : "secondary";
	const runProbes = () => {
		if (!chat?.chatId || probeLoading) return;
		probeLoading = true;
		probeReport = null;
		PRConnect.send("/api/Chat/runRoutingProbes", { chatId: chat.chatId }, (data) => {
			probeLoading = false;
			if (data.header.status === "success") probeReport = data.result;
		});
	};

	// ── 블록 겹침 경고: "이 두 블록이 섞일 수 있어요"를 알려줌. findBlockOverlaps는 LLM 호출이라
	//    저장마다 자동으로 돌리지 않고, 진입 시 1회 + 저작자가 [겹침 점검] 버튼으로 원할 때만 갱신한다. ──
	let overlaps = [];
	let overlapLoading = false;
	const loadOverlaps = () => {
		if (!chat?.chatId || overlapLoading) return;
		overlapLoading = true;
		PRConnect.send("/api/Chat/findBlockOverlaps", { chatId: chat.chatId }, (data) => {
			overlapLoading = false;
			if (data.header.status === "success") overlaps = data.result?.overlaps || [];
		});
	};
	// (보조) 추천대로 두 블록의 '이럴 때 보여주세요' 설명만 교체하고 저장.
	// 겹침을 직접 해소하는 액션이라, 두 저장이 끝난 뒤 loadOverlaps를 딱 1번만 호출(배너 갱신).
	const applyOverlapFix = (ov) => {
		const blkA = blocks.find((x) => x.id === ov.aId);
		const blkB = blocks.find((x) => x.id === ov.bId);
		let pending = 0;
		const done = () => { if (--pending === 0) loadOverlaps(); };
		if (blkA && ov.aRewrite) { blkA.description = ov.aRewrite; pending++; saveBlock(blkA, done); }
		if (blkB && ov.bRewrite) { blkB.description = ov.bRewrite; pending++; saveBlock(blkB, done); }
		blocks = blocks;
	};
	// (권장) 되묻는 허브 블록 자동 생성: A/B로 navigate 하는 버튼 블록을 만들어 애매함을 구조로 해소.
	//        허브가 navigate로 두 블록을 이으면 서버가 그 쌍 경고를 멈춘다(무한 경고 종료).
	// 생성은 LLM 호출이라 몇 초 걸림 → 진행 중 표시 + 중복 클릭 차단(안 그러면 허브가 여러 개 생김).
	let hubCreating = {}; // key(aId|bId) → true while creating
	const createHubForOverlap = (ov) => {
		if (!chat?.chatId) return;
		const key = ov.aId + "|" + ov.bId;
		if (hubCreating[key]) return; // 이미 만드는 중이면 무시
		hubCreating = { ...hubCreating, [key]: true };
		PRConnect.send(
			"/api/Chat/createDisambiguationHub",
			{ chatId: chat.chatId, aId: ov.aId, bId: ov.bId },
			(data) => {
				hubCreating = { ...hubCreating, [key]: false };
				if (data.header.status === "success") {
					loadBlocks();
					loadOverlaps();
				}
			},
		);
	};

	// ── 적합성 배지 피드백 루프: 프리뷰 배지 [바로잡기] 클릭 → 여기서 수신 ──
	// 테스트 중 발견한 오라우팅을 그 자리에서 저작 개선으로 잇는다:
	// 질문(발화)을 올바른 블록의 '이럴 때 보여주세요'에 예시로 추가하고 저장.
	// (런타임 CHAT의 ROUTING_FEEDBACK_MSG와 동일 문자열 — types/chat.ts 참고)
	const ROUTING_FEEDBACK_MSG = "AVATALK_ROUTING_FEEDBACK";
	let feedback = null; // { utterance, routing } | null → Sheet 표시
	const onRoutingFeedback = (e) => {
		const d = e.data;
		if (!d || typeof d !== "object" || d.type !== ROUTING_FEEDBACK_MSG) return;
		const iframe = iframeRefStore.get();
		if (!iframe || e.source !== iframe.contentWindow) return; // 프리뷰 iframe에서 온 것만
		feedback = { utterance: (d.utterance || "").trim(), routing: d.routing || null };
	};
	// 질문을 대상 블록 설명에 예시로 덧붙이고 저장(→ saveBlock cb가 겹침 경고 재점검까지 수행).
	const applyFeedbackTo = (target) => {
		if (!feedback?.utterance || !target?.id) return;
		const u = feedback.utterance.replace(/"/g, "'"); // 설명 안 따옴표 충돌 방지
		const desc = (target.description || "").trim();
		if (!desc.includes(u)) {
			target.description = desc ? `${desc} (예: "${u}")` : `(예: "${u}")`;
			saveBlock(target);
			blocks = blocks;
		}
		feedback = null;
	};

	const loadSchemas = () => {
		PRConnect.send("/api/Chat/getBlockSchemas", {}, (data) => {
			if (data.header.status === "success") schemas = data.result || {};
		});
	};
	const loadBlocks = () => {
		if (!chat?.chatId) return;
		PRConnect.send(
			"/api/Chat/getBlockList",
			{ chatId: chat.chatId },
			(data) => {
				if (data.header.status === "success") {
					blocks = (data.result || []).map((b) => {
						const contents = b.contents || [];
						// 하위호환: 아직 마이그레이션 안 된 블록(FIELDS 컬럼 O, form 콘텐츠 X)은
						// 로드 시 폼 콘텐츠로 변환해 콘텐츠 항목으로 보이게 한다(저장하면 CONTENTS로 동결).
						if (
							Array.isArray(b.fields) &&
							b.fields.length &&
							!contents.some((c) => c.type === "form")
						) {
							contents.push({
								type: "form",
								data: { fields: b.fields, completionMessage: "", submitLabel: "제출", cancelLabel: "취소" },
							});
						}
						return {
							...b,
							contents,
							buttons: b.buttons || [],
							fields: null, // 폼은 이제 CONTENTS에 산다(FIELDS 컬럼 미사용)
							completion: b.completion ?? null, // 완료조건(스텝, ⑨) — 있으면 스텝 블록
							_dirty: false,
						};
					});
				}
			},
		);
	};
	onMount(() => {
		loadSchemas();
		loadBlocks();
		loadFlows();
		loadCourses();
		loadAssetMap();
		loadOverlaps();
		// 프리뷰 배지 [바로잡기] 수신 (컴포넌트 해제 시 정리)
		window.addEventListener("message", onRoutingFeedback);
		return () => window.removeEventListener("message", onRoutingFeedback);
	});

	const markDirty = (block) => {
		block._dirty = true;
		block._buttonsDropped = 0;   // 고치는 중이면 지난 저장의 경고는 내린다(다음 저장에서 다시 센다)
		blocks = blocks;
		// 편집 중인(=프리뷰 중인) 블록이면 프리뷰를 실시간 갱신
		if (activePreviewBlock === block) postPreview(block);
	};
	const blankBlock = () => ({
		id: null,
		description: "",
		contents: [],
		buttons: [],
		terminal: false,
		isEntry: false,
		role: null,
		completion: null,
		sort: blocks.length,
		_dirty: true,
	});
	const addBlock = () => {
		blocks = [...blocks, blankBlock()];
	};

	// ── 테스트 챗 전용 생성 ──
	//    저작자가 "블록을 만들고 → 스텝으로 바꾸고 → 완료 유형을 고르는" 세 단계를 밟지 않게,
	//    필요한 형태로 바로 만들어 준다. 테스트 챗에서 만들 것은 이 둘뿐이다.
	const addProbeBlock = () => {
		const b = blankBlock();
		b.description = "레벨 확인";
		b.completion = {
			type: "probe", situation: "",
			resultKey: "", scoreKey: "", warmup: 2, stopAfter: 2,
			questions: [newProbeQ(1)],
			tiers: [{ from: 1, to: 3, label: "" }],
			maxTurns: 12, next: null,
		};
		blocks = [...blocks, b];
	};
	const addDoneBlock = () => {
		const b = blankBlock();
		b.description = "설정 완료 화면";
		b.role = "intakeDone";   // 저장 후 서버에 역할을 알린다(saveBlock 콜백에서)
		// AI 슬롯을 미리 하나 깔고 **지침 예시를 채워 둔다.**
		//   엔진은 방금 끝난 판정의 문답을 '참고 자료'로만 넘긴다 — 소감을 줄지 말지는 이 지침이 정한다.
		//   (코드에 "짚고 교정하고 격려하라"를 박으면 피드백을 안 줄 자유가 사라지고, 그 루브릭이
		//    어학 교육 모양이라 캐릭터 성향 설정 같은 챗에서는 틀린 지시가 된다)
		//   → 기본값은 '준다'로 두되 **지울 수 있는 문장**으로 준다. 안 주려면 이 칸을 바꿔 쓰면 된다.
		b.contents = [{
			type: "text",
			data: {
				mode: "ai",
				prompt: "확인이 끝났다고 알리고, 방금 오간 문답에서 잘한 점을 실제 표현 그대로 짚어 짧게 격려해줘.",
			},
		}];
		blocks = [...blocks, b];
	};

	// 판정 스텝의 결과 키를 **프로필 항목으로 자동 선언**한다.
	// ⚠️ 서버는 "TRAITS 에 write 로 선언한 키만 저장"이라(게이트 오염 방어) 이게 없으면 판정은
	//    도는데 값이 안 남는다 — 저작자에겐 "왜 저장이 안 되지"로만 보인다. 같은 키를 두 곳에
	//    적게 하는 대신, 판정 스텝이 선언한 키를 여기서 자동으로 얹는다(방어 규칙은 그대로).
	const syncProbeKeys = (block) => {
		const c = block?.completion;
		if (c?.type !== "probe") return;
		const want = [c.resultKey, c.scoreKey].map((k) => (k || "").trim()).filter(Boolean);
		if (!want.length) return;
		const have = new Set((chat.traits || []).map((t) => t.key));
		const added = want.filter((k) => !have.has(k)).map((k) => ({ key: k, desc: "레벨 판정 결과", write: true }));
		// 이미 있는 키는 write 만 켠다(저작자가 지시문을 걸어둔 항목을 덮지 않게)
		chat.traits = [
			...(chat.traits || []).map((t) => (want.includes(t.key) ? { ...t, write: true } : t)),
			...added,
		];
		chat = chat;
		chat.save();
	};

	// 첫 대화 블록 지정: 저장된 블록만 가능, 내용 저장과 별개로 즉시 서버 반영.
	const setEntryBlock = (block) => {
		if (!block.id) return;
		PRConnect.send(
			"/api/Chat/setEntryBlock",
			{ chatId: chat.chatId, blockId: block.id },
			(data) => {
				if (data.header.status === "success")
					blocks = blocks.map((b) => ({
						...b,
						isEntry: b.id === block.id,
					}));
			},
		);
	};

	// 사전 설정 완료 화면 지정(CHAT_BLOCKS.ROLE='intakeDone') — 첫 대화 블록 토글과 같은 규약.
	//   누르면 그 역할이 이 블록으로 옮겨가고(같은 챗의 다른 블록에서는 해제), 다시 누르면 해제된다.
	//   ⚠️ 평범한 블록과 똑같이 렌더되므로 AI 텍스트 슬롯에 "사전 설정에서 받은 값으로 인사" 같은
	//      지침을 쓰면 그 값이 문장으로 들어간다(고정 문구·이미지도 물론 된다).
	const setIntakeDoneBlock = (block) => {
		if (!block.id) return;
		const next = block.role === "intakeDone" ? null : block.id;   // 다시 누르면 해제
		PRConnect.send(
			"/api/Chat/setBlockRole",
			{ chatId: chat.chatId, blockId: next, role: "intakeDone" },
			(data) => {
				if (data.header.status === "success")
					blocks = blocks.map((b) => ({
						...b,
						role: b.id === next ? "intakeDone" : b.role === "intakeDone" ? null : b.role,
					}));
			},
		);
	};

	// text 슬롯 모드: 'ai'(2차 LLM이 채움) / 'static'(고정 문구). AI 슬롯은 블록당 1개 제한.
	const isAiText = (c) =>
		c.type === "text" && (c.data?.mode ?? "ai") !== "static";
	const hasAiText = (block, exceptIdx = -1) =>
		block.contents.some((c, i) => i !== exceptIdx && isAiText(c));
	const setTextMode = (block, ci, mode) => {
		if (mode === "ai" && hasAiText(block, ci)) {
			alert(
				"AI 생성 텍스트는 블록당 1개만 가능합니다.\n기존 AI 텍스트를 '직접 입력'으로 바꾼 뒤 시도하세요.",
			);
			return;
		}
		block.contents[ci].data = { ...block.contents[ci].data, mode };
		markDirty(block);
	};

	// 콘텐츠 추가/삭제/순서변경
	const addContent = (block, type) => {
		if (!type) return;
		// 폼은 블록당 1개(제출이 이 블록으로 매핑되므로 2개면 모호). 이미 있으면 무시.
		if (type === "form" && block.contents.some((c) => c.type === "form")) return;
		// 새 텍스트는 기본 'ai', 단 이미 AI 슬롯이 있으면 'static'으로 (블록당 AI 1개)
		const data =
			type === "text"
				? { mode: hasAiText(block) ? "static" : "ai" }
				: type === "form"
					? { fields: [], completionMessage: "", submitLabel: "제출", cancelLabel: "취소" }
					: type === "map"
						? { provider: "kakao", name: "", address: "" }
						: {};
		block.contents = [...block.contents, { type, data }];
		markDirty(block);
	};
	const removeContent = (block, i) => {
		block.contents = block.contents.filter((_, idx) => idx !== i);
		markDirty(block);
	};

	// ── 입력폼 콘텐츠(form): 고객이 값을 입력해 [제출]하는 위젯. 폼은 콘텐츠 항목의 한 타입이다.
	//    data = { completionMessage, submitLabel, cancelLabel, fields:[{key,ask,type,required,options?}] }.
	//    값은 런타임 [제출] 시 formSubmit으로 서버에 전달, 서버가 completionMessage로 완료 멘트를 낸다.
	// 필드 입력 타입: 런타임이 타입별 입력 위젯을 렌더. select는 options(선택지) 필요.
	const FIELD_TYPES = [
		{ value: "text", label: "텍스트" },
		{ value: "email", label: "이메일" },
		{ value: "phone", label: "전화번호" },
		{ value: "date", label: "날짜" },
		{ value: "time", label: "시간" },
		{ value: "select", label: "선택지" },
	];
	const addFormField = (block, content) => {
		content.data.fields = [...(content.data.fields || []), { key: "", ask: "", type: "text", required: true }];
		markDirty(block);
	};
	const removeFormField = (block, content, i) => {
		content.data.fields = (content.data.fields || []).filter((_, idx) => idx !== i);
		markDirty(block);
	};
	const moveFormField = (block, content, i, dir) => {
		const arr = content.data.fields || [];
		const j = i + dir;
		if (j < 0 || j >= arr.length) return;
		[arr[i], arr[j]] = [arr[j], arr[i]];
		content.data.fields = arr;
		markDirty(block);
	};
	// select 타입 필드의 선택지(options) 편집
	const addFieldOption = (block, field) => {
		field.options = [...(field.options || []), ""];
		markDirty(block);
	};
	const removeFieldOption = (block, field, i) => {
		field.options = (field.options || []).filter((_, idx) => idx !== i);
		markDirty(block);
	};

	// ── 유도 목표(스텝, ⑨): 블록을 "스텝"으로 만들면 completion을 갖는다.
	//    completion = { type:'objectives', objectives:[{id,criterion}], minCovered, maxTurns, next }
	//    스텝은 진입하면 그 대화를 소유해 목표를 유도하고, 다 채우면 next로 전진(같은 블록이 다시 안 나옴).
	const genObjId = () => "o" + Math.random().toString(36).slice(2, 8);
	// 스텝 = 완료 조건(completion)을 가진 블록. 완료 유형은 objectives(유도 목표) | judge(대화 채점 평가).
	// ⚠️ 서버는 "completion이 있으면 스텝"(CompletionRegistry) — 여기 빠뜨리면 자유 블록 뷰에 스텝이 섞여 보인다.
	//    실제로는 서버 getRoutingCatalog(COMPLETION IS NULL)가 걸러서 그 블록은 FAQ로 매칭되지도 않는다.
	const STEP_TYPES = ["objectives", "converse", "judge", "eval", "probe"];
	const isStep = (block) => !!block.completion && STEP_TYPES.includes(block.completion.type);
	// 스텝을 열 때 나가는 말 = 첫 AI 텍스트 콘텐츠(서버 BlockAssembler가 그 슬롯을 채워 렌더).
	// 그 칸에만 "여는 말"이라는 안내를 붙인다(나머지 AI 텍스트는 평범한 슬롯).
	const firstAiTextIndex = (block) => (block?.contents || []).findIndex(isAiText);
	const enableStep = (block) => {
		block.completion = {
			type: "objectives",
			situation: "",   // [이 스텝의 상황] — 대화 내내 참고. 비우면 콘텐츠 슬롯을 폴백으로 읽는다
			objectives: [{ id: genObjId(), criterion: "" }],
			minCovered: null,
			maxTurns: 6,
			next: null,
		};
		markDirty(block);
	};
	// 예전 '추가 상황 설명'(situationNote) 정리 — 칸을 없앤 뒤에도 DB에 남아 있는 값을 저작자가 처리하게.
	const mergeLegacyNote = (block) => {
		const c = block.completion;
		const note = (c.situationNote || "").trim();
		if (!note) return;
		c.situation = [(c.situation || "").trim(), note].filter(Boolean).join("\n");
		delete c.situationNote;
		markDirty(block);
	};
	const dropLegacyNote = (block) => {
		delete block.completion.situationNote;
		markDirty(block);
	};
	// ── 판정 수집(probe) 저작: 문항 하나 = 레벨 하나 ──
	//    쉬운 것부터 순서대로 묻고, 통과한 가장 높은 문항의 레벨이 결과가 된다.
	//    warmup   = 앞 N문항은 채점하지 않는다(첫 질문이 어려우면 위축돼 이후 답이 전부 짧아진다).
	//    stopAfter= 연속 N개 미달이면 중단(초급 학습자가 어려운 문항을 끝까지 겪지 않게).
	//    tiers    = 숫자를 사람이 읽는 이름으로. 대화 지시문은 이 이름(구간)에 걸면 몇 벌로 끝난다.
	//    ⚠️ 모범답안은 "전형적인 통과 예시"로 쓰인다 — 지나치게 다듬은 문장을 넣으면 그게 통과
	//       하한선처럼 작용해 실제로 그 수준인 학습자가 무더기로 미달한다.
	const newProbeQ = (level) => ({ level, text: "", sample: "", criteria: "" });
	const addProbeQ = (block) => {
		const c = block.completion;
		const next = (c.questions || []).reduce((m, q) => Math.max(m, Number(q.level) || 0), 0) + 1;
		c.questions = [...(c.questions || []), newProbeQ(next)];
		markDirty(block);
	};
	const removeProbeQ = (block, i) => {
		block.completion.questions = (block.completion.questions || []).filter((_, k) => k !== i);
		markDirty(block);
	};
	const addTier = (block) => {
		const c = block.completion;
		const last = (c.tiers || [])[(c.tiers || []).length - 1];
		const from = last ? (Number(last.to) || 0) + 1 : 1;
		c.tiers = [...(c.tiers || []), { from, to: from + 2, label: "" }];
		markDirty(block);
	};
	const removeTier = (block, i) => {
		block.completion.tiers = (block.completion.tiers || []).filter((_, k) => k !== i);
		markDirty(block);
	};

	// ── 사후 점검: 사전 테스트와 같은 문항(completion.sameAs) ──
	//    허브 빌더 [사전 테스트로 만들기]가 만든 판정 블록은 문항을 갖지 않고 사전 테스트의 판정 블록을 가리킨다.
	//    서버(BlockStore._resolveSameAs)가 읽을 때마다 원본 규칙을 덮어쓰므로, 사전을 고치면 사후도 같이 바뀌어
	//    사전·사후 비교가 깨지지 않는다. 여기서는 원본을 읽어 **보여주기만** 한다(고치는 곳은 사전 테스트).
	let linkedProbe = null;      // 원본 판정 블록
	let linkedProbeKey = null;   // 마지막으로 읽은 원본(chatId:blockId) — 같은 원본을 매번 다시 읽지 않게
	$: linkRef = probeBlock?.completion?.sameAs ?? null;
	$: if (linkRef?.chatId && `${linkRef.chatId}:${linkRef.blockId}` !== linkedProbeKey) loadLinkedProbe(linkRef);
	const loadLinkedProbe = (ref) => {
		linkedProbeKey = `${ref.chatId}:${ref.blockId}`;
		linkedProbe = null;
		PRConnect.send("/api/Chat/getBlockList", { chatId: ref.chatId }, (data) => {
			const list = data.result || [];
			// 서버와 같은 규칙: 가리킨 블록 → 없으면(다시 만들었으면) 그 챗에서 문항을 직접 가진 판정 블록
			const own = (b) => b?.completion?.type === "probe" && !b.completion.sameAs;
			linkedProbe = list.find((b) => b.id === ref.blockId && own(b)) ?? list.find(own) ?? null;
		});
	};
	// 연결 끊기 = 지금 원본 규칙을 **복사해** 이 블록 것으로 만든다. 그 뒤로는 사전 테스트를 고쳐도 안 따라온다.
	const unlinkProbe = (block) => {
		if (!linkedProbe) return;
		if (!confirm("연결을 끊으면 지금 문항을 복사해 이 채팅에서 따로 씁니다.\n이후 사전 테스트를 고쳐도 여기는 바뀌지 않아 사전·사후 비교가 어긋날 수 있어요. 끊을까요?")) return;
		const copy = JSON.parse(JSON.stringify(linkedProbe.completion));
		delete copy.sameAs;
		block.completion = { ...copy, type: "probe" };
		markDirty(block);
	};

	// 완료 유형 전환(유도 목표 ↔ 자유 대화 ↔ 평가). 공통(maxTurns·next)은 유지, 선택한 타입의 필드만 보강.
	const changeCompletionType = (block) => {
		const c = block.completion;
		if (!c) return;
		if (c.situation == null) c.situation = "";   // 유형과 무관한 공통 필드
		if (c.type === "judge") {
			if (c.rubric == null) c.rubric = "";
			if (c.passScore == null) c.passScore = 60;
		} else if (c.type === "probe") {
			// 판정 수집(레벨 확인): 문항 하나가 레벨 하나다. 쉬운 것부터 물어 올라가고,
			// 통과한 가장 높은 문항의 레벨이 결과가 된다.
			if (!c.resultKey) c.resultKey = "";
			if (!c.scoreKey) c.scoreKey = "";
			if (c.warmup == null) c.warmup = 2;
			if (c.stopAfter == null) c.stopAfter = 3;
			if (!Array.isArray(c.questions) || !c.questions.length) c.questions = [newProbeQ(1)];
			if (!Array.isArray(c.tiers) || !c.tiers.length) c.tiers = [{ from: 1, to: 3, label: "" }];
		} else if (c.type === "converse") {
			// 자유 대화: 목표 대신 "몇 번 주고받으면 완료" + "무슨 얘기를 하는 자리인지"
			if (c.minTurns == null) c.minTurns = 3;
		} else {
			if (!Array.isArray(c.objectives) || !c.objectives.length)
				c.objectives = [{ id: genObjId(), criterion: "" }];
			if (c.minCovered === undefined) c.minCovered = null;
		}
		markDirty(block);
	};
	// 스텝 해제 = 완료 조건을 떼어 "그냥 블록(자유 블록)"으로 되돌리는 것.
	// ⚠️ 플로우에 남겨두면 모순 상태가 된다: 서버는 completion이 없으면 스텝으로 보지 않아
	//    그 자리에 도달해도 그냥 지나가고(ActiveStepHandler → FAQ 위임), 동시에 자유 라우팅
	//    카탈로그(COMPLETION IS NULL)에 편입돼 엉뚱한 데서 불려 나온다.
	//    → 해제하면 플로우에서도 함께 뺀다(그 블록은 자유 블록 목록으로 이동).
	const disableStep = (block) => {
		const inFlow = !!currentFlow?.steps?.includes(block.id);
		const msg = inFlow
			? "완료 조건을 떼고 '자유 블록'으로 되돌립니다.\n이 레슨에서도 빠지고 자유 블록 목록으로 이동해요. 계속할까요?"
			: "완료 조건을 떼고 '자유 블록'으로 되돌립니다. 계속할까요?";
		if (!confirm(msg)) return;
		block.completion = null;
		markDirty(block);
		if (inFlow) removeStepById(block.id);
	};
	const addObjective = (block) => {
		block.completion.objectives = [...(block.completion.objectives || []), { id: genObjId(), criterion: "" }];
		markDirty(block);
	};
	const removeObjective = (block, i) => {
		block.completion.objectives = (block.completion.objectives || []).filter((_, idx) => idx !== i);
		markDirty(block);
	};
	const moveObjective = (block, i, dir) => {
		const arr = block.completion.objectives || [];
		const j = i + dir;
		if (j < 0 || j >= arr.length) return;
		[arr[i], arr[j]] = [arr[j], arr[i]];
		block.completion.objectives = arr;
		markDirty(block);
	};
	// ── 장면 정보(completion.facts) + 목표의 [알아낼 것](objective.find) — 서버 blockUtil.readStepFacts.
	//    캐릭터만 아는 정보(가격·시간·생각). [알아낼 것]으로 고른 건 학습자가 물을 때까지 봇이 먼저 말하지 않고,
	//    물어서 알아내면 그 목표가 채워진다. 고르지 않은 정보(메뉴처럼 고를 거리)는 봇이 자연스럽게 쓴다.
	const addFact = (block) => {
		block.completion.facts = [...(block.completion.facts || []), { key: "", value: "" }];
		markDirty(block);
	};
	const removeFact = (block, i) => {
		const key = (block.completion.facts || [])[i]?.key;
		block.completion.facts = (block.completion.facts || []).filter((_, idx) => idx !== i);
		// 지운 정보를 가리키던 [알아낼 것]도 뗀다 — 남겨 두면 서버가 없는 정보를 기다린다
		if (key) for (const o of block.completion.objectives || []) if (Array.isArray(o.find)) o.find = o.find.filter((k) => k !== key);
		block.completion.objectives = block.completion.objectives;
		markDirty(block);
	};
	const toggleFind = (block, obj, key) => {
		const cur = Array.isArray(obj.find) ? obj.find : [];
		obj.find = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
		block.completion.objectives = block.completion.objectives;
		markDirty(block);
	};
	const factKeys = (c) => (c?.facts || []).map((f) => String(f?.key || "").trim()).filter(Boolean);

	// ── 유도 방식(스탠스, guideStyle): 유도(objectives/eval-self) 스텝에서 AI가 "어떻게 이끌지".
	//    서버 chat-core/block/guideStyles.js 프리셋과 대응. 비우면(=undefined) 챗 기본값(추후)→내장 minimal.
	const GUIDE_STYLE_OPTIONS = [
		{ key: "", label: "기본값(챗 설정 따름)" },
		{ key: "coach", label: "코칭·유도형 — 정답 대신 스스로 하게, 막히면 부분 힌트" },
		{ key: "direct", label: "직접 안내형 — 다음 할 일·예시를 직접 제시" },
		{ key: "minimal", label: "최소 개입형 — 자연스럽게 목표로 이끌기" },
		{ key: "custom", label: "직접 작성…" },
	];
	// completion.guideStyle 세터: ''=상속(필드 제거) / custom=텍스트 유지 / 그 외=프리셋 키. (호출부가 dirty 트리거)
	const setGuideStylePreset = (completion, preset) => {
		if (!completion) return;
		if (!preset) delete completion.guideStyle;
		else if (preset === "custom") completion.guideStyle = { preset: "custom", custom: completion.guideStyle?.custom || "" };
		else completion.guideStyle = { preset };
	};
	// ── 유도문형(initiative): 개입 강도 사다리(서버 chat-core/block/stepLadder.js)의 "시작 단".
	//    유도 방식(guideStyle)이 "어떻게 도와주나(속도·상한)"라면, 이건 "누가 먼저 말하나".
	//    배타 분류가 아니라 같은 축의 눈금이라 조금 어긋나게 골라도 정체하면 엔진이 알아서 올린다.
	const INITIATIVE_OPTIONS = [
		{ key: "", label: "기본값(질문형)" },
		{ key: "wait", label: "기다림형 — 상황만 만들고 사용자가 먼저 말하길 기다림" },
		{ key: "ask", label: "질문형 — AI가 먼저 물어 이끌어냄" },
		{ key: "demo", label: "시범형 — AI가 먼저 해 보이고 사용자가 이어받음" },
	];
	// ── 저작 점검(LLM): 정적 린트가 규칙으로 판정할 수 없는 "이 목표가 이 상황에서 자연스러운가"만 물어본다.
	//    편집 중인 블록을 그대로 보내므로 저장 전에도 점검된다(서버 /api/Chat/reviewStep, DB 미변경).
	let reviewBlock = null;
	let reviewResult = null;
	let reviewLoading = false;
	const runReview = (block) => {
		reviewBlock = block;
		reviewResult = null;
		reviewLoading = true;
		PRConnect.send(
			"/api/Chat/reviewStep",
			{
				chatId: chat.chatId,
				block: {
					description: block.description,
					contents: block.contents,
					completion: block.completion,
				},
			},
			(data) => {
				reviewLoading = false;
				if (data.header.status === "success") reviewResult = data.result;
				else alert("점검에 실패했어요. 잠시 후 다시 시도해 주세요.");
			},
		);
	};

	// 목표 입력 예시를 "먼저 고른 유도 방식·문형"에 맞춘다.
	//   코칭은 막혔을 때 부분 힌트를 주므로 목표 끝의 "(예시 답)"이 힌트 재료가 된다(서버 _goalOnly가 유도용에선 감춤).
	//   기다림형은 사용자가 먼저 입을 떼는 게 목표라 "먼저 ~한다" 형태가 맞는다.
	const objectivePlaceholder = (completion) => {
		const style = completion?.guideStyle?.preset || completion?.guideStyle || "";
		const iv = completion?.initiative || "ask";
		if (style === "coach")
			return "사용자가 해낼 일 + 끝에 (예시 답) — 예: 학습자가 영어로 음료를 주문한다 (I'd like a latte.)";
		if (iv === "wait")
			return "사용자가 먼저 해낼 일 — 예: 학습자가 점원에게 먼저 말을 건다";
		return "사용자가 해낼 일(겉으로 드러나게) — 예: 학습자가 자기 이름을 말한다";
	};

	// completion.initiative 세터: ''=기본값(필드 제거) / 그 외=프리셋 키. (호출부가 dirty 트리거)
	const setInitiative = (completion, key) => {
		if (!completion) return;
		if (!key) delete completion.initiative;
		else completion.initiative = key;
	};

	// ── 챗 전체 설정(용도·학습자 정보·수집 항목·유도 방식·쓰는 말/받아 주기)은 [대화 설정] 탭
	//    (ConversationSettingsContext.svelte)에 있다. 여기는 스텝 덮어쓰기와, 무엇을 덮는지 보이는 요약만.
	const goSettings = () => chat.changeCurrentTab("conversation");
	// 요약 줄의 챗 기본 유도 방식 이름. 비면(null) 서버 내장 minimal.
	const guideStyleLabel = (v) =>
		v ? (GUIDE_STYLE_OPTIONS.find((o) => o.key === v)?.label || v).split(" — ")[0] : "최소 개입형(기본값)";

	// ── 스텝의 쓰는 말·학습자 말 받아 주기(completion.reply) — 축 단위로 챗 기본값을 덮어쓴다(블록 저장 때 함께).
	const setStepReply = (block, v) => {
		if (v) block.completion.reply = v;
		else delete block.completion.reply;
		markDirty(block);
	};

	// ── 레벨별 낱말(스텝) ─────────────────────────────────────────────────────────
	// 학습자별 내용(variants)의 한 가지 — 이름이 정해져 있고 기준은 연결된 레벨 항목이다.
	// 콘텐츠 파이프라인(content/globalcenter/tools/lib.js WORDS_VAR)도 같은 이름·같은 줄을 만든다 → 그쪽 레슨도 이 칸에 그대로 보인다.
	const LEVEL_WORDS_VAR = "words";
	// 줄에는 **재료만** 둔다. 어떻게 건넬지(하나씩/둘 중 고르게/앞부분만)는 유도 방식(guideStyle)이 정한다 —
	//   예전 줄의 "막혔을 때 하나씩 건넨다"가 코칭형의 "고를 거리 두 개"와 같은 프롬프트에서 부딪혔다
	//   (설계 project-le868/conversation-settings-design.md §4). 콘텐츠 파이프라인 templates.json lesson.situation 과 같은 줄.
	const LEVEL_WORDS_LINE = "[쓸 만한 낱말 — 이 학습자 수준에 맞춘 것] {{words}}";
	// 예전 줄 — 비우기(clearLevelWords)가 옛 스텝에서도 줄을 걷어 내게 함께 지운다
	const LEGACY_LEVEL_WORDS_LINES = ["[쓸 만한 낱말 — 이 학습자 수준에 맞춘 것. 막혔을 때 하나씩 건넨다. 문장으로 늘어놓지 않는다] {{words}}"];
	$: levelDecl = (chat.traits || []).find((t) => t.from?.chatId && knownValues(t).length) || null;

	// ── {{변수}} · 학습자별 내용(스텝) ─────────────────────────────────────────────
	// 지침에 쓸 수 있는 입력 = 닉네임(기본 제공) + 학습자 정보 항목 키들.
	$: inputKeys = [NICKNAME_KEY, ...(chat.traits || []).map((t) => (t.key || "").trim()).filter((k) => k && k !== NICKNAME_KEY)];
	// 학습자별 내용을 고를 기준이 될 수 있는 항목 = 닉네임을 뺀 학습자 정보(값이 몇 가지로 정해진 것).
	$: variantByKeys = inputKeys.filter((k) => k !== NICKNAME_KEY);
	const traitByKey = (k) => (chat.traits || []).find((t) => t.key === k);
	const variants = (block) => (Array.isArray(block?.completion?.variants) ? block.completion.variants : []);
	// 기준 항목을 고르면 그 항목이 가질 수 있는 값마다 칸을 미리 만들어 둔다(빈칸이면 점검이 잡는다).
	const seedMap = (by, old = {}) => {
		const vals = knownValues(traitByKey(by));
		const m = {};
		for (const v of vals) m[v] = old[v] ?? "";
		for (const [v, t] of Object.entries(old)) if (!(v in m) && (t || "").trim()) m[v] = t;   // 지운 값의 내용은 버리지 않는다
		return m;
	};
	const addVariant = (block) => {
		const by = variantByKeys.find((k) => knownValues(traitByKey(k)).length) || variantByKeys[0] || "";
		const used = new Set(variants(block).map((v) => v.name));
		let name = "hints", n = 2;
		while (used.has(name)) name = `hints${n++}`;
		block.completion.variants = [...variants(block), { name, by, map: seedMap(by) }];
		markDirty(block);
	};
	const setVariant = (block, i, patch) => {
		const list = variants(block).slice();
		const cur = { ...list[i], ...patch };
		if (patch.by !== undefined) cur.map = seedMap(patch.by, list[i].map || {});
		list[i] = cur;
		block.completion.variants = list;
		markDirty(block);
	};
	const setVariantText = (block, i, val, text) => {
		const list = variants(block).slice();
		list[i] = { ...list[i], map: { ...(list[i].map || {}), [val]: text } };
		block.completion.variants = list;
		markDirty(block);
	};
	const removeVariant = (block, i) => {
		block.completion.variants = variants(block).filter((_, k) => k !== i);
		if (!block.completion.variants.length) delete block.completion.variants;
		markDirty(block);
	};
	const levelWords = (block) => variants(block).find((v) => v.name === LEVEL_WORDS_VAR) || null;
	const setLevelWords = (block, level, text) => {
		if (!levelDecl) return;
		const list = variants(block).slice();
		let i = list.findIndex((v) => v.name === LEVEL_WORDS_VAR);
		if (i < 0) {
			list.push({ name: LEVEL_WORDS_VAR, by: levelDecl.key, map: {} });
			i = list.length - 1;
		}
		list[i] = { ...list[i], by: levelDecl.key, map: { ...(list[i].map || {}), [level]: text } };
		block.completion.variants = list;
		// 지침이 {{words}} 를 안 쓰면 AI 에게 안 간다 — 처음 채울 때 [이 스텝의 상황] 끝에 한 줄 붙인다.
		// 숨은 동작이 아니라 상황 칸에 보이는 글이라 저작자가 옮기거나 고칠 수 있다.
		const sit = block.completion.situation || "";
		if (text.trim() && !/\{\{\s*words\s*\}\}/.test(sit)) {
			block.completion.situation = `${sit}${sit && !sit.endsWith("\n") ? "\n" : ""}${LEVEL_WORDS_LINE}`;
		}
		markDirty(block);
	};
	const clearLevelWords = (block) => {
		block.completion.variants = variants(block).filter((v) => v.name !== LEVEL_WORDS_VAR);
		if (!block.completion.variants.length) delete block.completion.variants;
		let sit = block.completion.situation || "";
		for (const line of [LEVEL_WORDS_LINE, ...LEGACY_LEVEL_WORDS_LINES]) sit = sit.replace(`\n${line}`, "").replace(line, "");
		block.completion.situation = sit;
		markDirty(block);
	};
	const levelWordsHint = (i, n) =>
		i === 0 ? "가장 쉬운 낱말 — 쉼표로 나눠서" : i === n - 1 ? "더 풍부한 표현 — 짧은 구·연결어까지" : "조금 더 넓은 낱말·짧은 구";

	// ── [프리뷰에서 이 레슨부터] — 레슨 첫 스텝에서 새 대화를 시작한다.
	//    ChatBuilder.resetPreview 가 [대화 리셋]과 같은 정리(토큰·이 챗 진도·수집·챗 울타리 학습자 정보)를 먼저 하고
	//    시작 블록만 바꾼다 — 대화를 남긴 채 뛰어들면 이미 해 본 레슨은 '복귀'로 이어져 처음부터 볼 수 없다.
	//    평가 유닛엔 두지 않는다: 직전 레슨 대화가 없으면 돌아볼 게 없다(레슨부터 시작하면 끝나고 평가로 이어진다).
	//    ⚠️ 프리뷰는 **저장된 초안**을 읽는다 → 저장 안 한 변경이 있으면 막는다(눌러도 고친 게 안 보여 헷갈린다).
	const lessonFirstStep = (flow) => (flow?.steps || [])[0] || null;
	const lessonUnsaved = (flow, list) => !!flow?._dirty || (flow?.steps || []).some((id) => list.find((b) => b.id === id)?._dirty);
	const previewFromTitle = (flow, list) =>
		!flow?.id || !lessonFirstStep(flow)
			? "저장된 레슨에 스텝이 있어야 시작할 수 있어요"
			: lessonUnsaved(flow, list)
				? "저장한 뒤 눌러 주세요 — 프리뷰는 저장된 내용으로 돌아요"
				: "이 챗의 프리뷰 대화·진도를 비우고 이 레슨부터 새로 시작해요";
	const previewFromLesson = (flow) => {
		const id = lessonFirstStep(flow);
		if (id && flow?.id && !lessonUnsaved(flow, blocks)) chat?.previewFrom?.(id);
	};

	// ── 장면 유형(sceneTypes.js ↔ 서버 chat-core/block/sceneTypes.js) — 레슨 설계라 스텝에서만 정한다.
	//    유형별 규칙("세부는 사용자가 정한다", "역할로만 말한다" 등)은 엔진이 싣는다. 상황 칸에는 무대만.
	//    지정 안 함이면 필드를 지운다 → 엔진이 아무것도 싣지 않는다(이 설정을 안 쓰는 챗은 그대로).
	const setSceneType = (block, type) => {
		const def = SCENE_TYPES.find((t) => t.key === type);
		if (!def) delete block.completion.scene;
		else block.completion.scene = def.needsRole ? { type, role: block.completion.scene?.role || "" } : { type };
		markDirty(block);
	};
	const setSceneRole = (block, role) => {
		block.completion.scene = { ...(block.completion.scene || {}), role };
		markDirty(block);
	};

	// 칩을 누르면 [이 스텝의 상황] 끝에 {{이름}} 을 붙인다(커서 위치 삽입보다 단순하고, 붙인 뒤 옮기면 된다).
	const insertVar = (block, name) => {
		const cur = block.completion.situation || "";
		block.completion.situation = `${cur}${cur && !/\s$/.test(cur) ? " " : ""}{{${name}}}`;
		markDirty(block);
	};

	// '담기…' 드롭다운은 고르는 즉시 동작하고 플레이스홀더로 되돌아간다(선택 상태를 남기지 않음).
	// ⚠️ <Select>는 내부에서 bind:value를 쓰므로 DOM 값만 지우면 리렌더 때 이전 선택이 되살아난다 → 상태를 비운다.
	let pickFlowForCourse = "";
	let pickStepForFlow = "";

	// 테스트 챗(사전/사후)이면 저작 화면이 통째로 달라진다 — 판정 기준 + 완료 화면 둘뿐.
	$: isTestChat = chat.purpose === "intake" || chat.purpose === "exit";

	// ── ⑨ 플로우(커리큘럼): 스텝(유도 목표를 가진 블록)을 순서대로 묶은 레슨.
	//    진행도 저장/재개/진행바의 근거(CHAT_FLOWS). 자유 블록(FAQ)과 한 챗에 공존한다.
	let viewMode = "blocks"; // 'blocks'(자유 블록) | 'flows'(플로우)
	let flows = [];
	let currentFlowIdx = 0; // 플로우 뷰에서 편집 중인 플로우
	const loadFlows = () => {
		if (!chat?.chatId) return;
		PRConnect.send("/api/Chat/getFlowList", { chatId: chat.chatId }, (data) => {
			if (data.header.status === "success") {
				flows = (data.result || []).map((f) => ({ ...f, steps: f.steps || [], _dirty: false }));
			}
		});
	};
	// 스텝 블록(완료조건 있는 것)만 플로우에 담긴다.
	// ⑨ 유닛 종류: flow.policy.kind === 'eval'이면 평가 유닛(코스 전용, 대화를 채점) / 아니면 레슨 유닛.
	//    [플로우]탭은 레슨 유닛만 보여준다 — 평가는 [코스]탭에서 레슨들 사이사이에 배치·저작한다.
	const isEvalUnit = (flow) => flow?.policy?.kind === "eval";
	$: lessonFlows = flows.filter((f) => !isEvalUnit(f));
	$: stepBlocks = blocks.filter((b) => b.id && isStep(b));
	$: currentFlow = lessonFlows[currentFlowIdx] ?? lessonFlows[0] ?? null;
	// 플로우 뷰: 그 플로우의 스텝 블록들을 순서대로. 자유 블록 뷰: FAQ(스텝 아님)만 — 스텝 블록은
	//   플로우 전용이라 자유 블록 뷰에 섞이지 않는다(서버 getRoutingCatalog=COMPLETION IS NULL 과 동일 모델).
	// ⚠️루프가 "블록 객체"를 직접 순회한다({@const} 래퍼로 감싸면 bind:가 막힘). 스텝 껍데기는
	//   viewMode·인덱스로 따로 얹는다. 블록 카드 마크업은 그대로(bind 유지).
	// 테스트 챗(사전/사후)에서 저작할 것은 딱 둘이다 — 판정 기준(probe 스텝)과 완료 화면.
	//   ⚠️ 플로우에 넣지 않는다. 판정은 레슨 커리큘럼이 아니라 단독 측정이라 플로우가 필요 없다
	//      (플로우를 먼저 만들어야 스텝을 저작할 수 있던 동선이 여기서 끊긴다).
	$: probeBlock = blocks.find((b) => b.completion?.type === "probe") ?? null;
	$: doneBlock = blocks.find((b) => b.role === "intakeDone") ?? null;
	// ⚠️ 테스트 챗이어도 **블록을 숨기지 않는다.** 역할이 옮겨간 옛 완료 화면이 화면에서 사라지면
	//    저작자는 지울 수도 없고 "없어졌다"고 또 만들어 중복만 쌓인다(실제로 4개까지 쌓였다).
	//    판정 블록을 맨 앞에 두고 나머지를 뒤에 붙인다.
	//    순서를 못 박는다: ①테스트 진행 → ②끝나면 보여줄 화면 → (그 외). SORT 에 맡기면
	//    옛 블록이 사이에 끼어 ①②가 뒤죽박죽으로 읽힌다.
	$: displayBlocks = isTestChat
		? [probeBlock, doneBlock, ...blocks.filter((b) => b !== probeBlock && b !== doneBlock)].filter(Boolean)
		: viewMode === "flows"
			? (currentFlow?.steps ?? []).map((id) => blocks.find((b) => b.id === id)).filter(Boolean)
			: viewMode === "courses"
				? [] // 코스 뷰는 블록 카드 대신 레슨(플로우) 카드를 보여준다
				: blocks.filter((b) => !isStep(b)); // 자유 블록 뷰 = 스텝 아닌 FAQ 블록만
	// [스텝 추가] 드롭다운: 아직 이 플로우에 없는 유도(objectives) 스텝. 평가(judge) 스텝은 코스의 평가 유닛에서 저작.
	$: stepsNotInFlow = stepBlocks.filter((b) => b.completion?.type === "objectives" && !(currentFlow?.steps ?? []).includes(b.id));
	const blockLabel = (id) => {
		const b = blocks.find((x) => x.id === id);
		return b ? `#${blocks.indexOf(b) + 1} · ${truncate(b.description || "(설명 없음)")}` : id;
	};
	const markFlowDirty = (flow) => { flow._dirty = true; flows = flows; };
	const blankFlow = () => ({ id: null, chatId: chat.chatId, title: "", steps: [], policy: null, sort: flows.length, _dirty: true });
	const addFlow = () => { flows = [...flows, blankFlow()]; currentFlowIdx = lessonFlows.length; }; // 새 레슨 = lessonFlows 끝(옛 길이=새 인덱스)
	const saveFlow = (flow, onDone) => {
		const payload = { chatId: chat.chatId, title: flow.title, steps: flow.steps, policy: flow.policy ?? null, sort: flow.sort ?? 0 };
		const isCreate = !flow.id;
		const cb = (data) => {
			if (data.header.status === "success") {
				if (isCreate && typeof data.result === "string") flow.id = data.result;
				flow._dirty = false;
				flows = flows;
				if (onDone) onDone();
			}
		};
		if (flow.id) PRConnect.send("/api/Chat/modifyFlow", { ...payload, id: flow.id }, cb);
		else PRConnect.send("/api/Chat/createFlow", payload, cb);
	};
	const deleteFlow = (flow) => {
		const done = () => {
			flows = flows.filter((f) => f !== flow);   // 신원 기반(레슨/평가 필터로 인덱스가 어긋날 수 있어 인덱스 삭제 금지)
			currentFlowIdx = Math.min(currentFlowIdx, Math.max(0, lessonFlows.length - 2));
		};
		if (!flow.id) { done(); return; }
		if (!confirm("이 플로우를 삭제할까요? (블록 자체는 지워지지 않아요)")) return;
		PRConnect.send("/api/Chat/deleteFlow", { id: flow.id }, (data) => { if (data.header.status === "success") done(); });
	};
	// 스텝 순서/구성(현재 플로우, blockId 기준). 변경 후 saveFlow(currentFlow)로 저장.
	const addExistingStepToFlow = (blockId) => {
		if (!blockId || !currentFlow) return;
		currentFlow.steps = [...currentFlow.steps, blockId];
		markFlowDirty(currentFlow);
	};
	const removeStepById = (blockId) => {
		if (!currentFlow) return;
		currentFlow.steps = currentFlow.steps.filter((s) => s !== blockId);
		markFlowDirty(currentFlow);
	};
	const moveStepById = (blockId, dir) => {
		if (!currentFlow) return;
		const arr = currentFlow.steps;
		const i = arr.indexOf(blockId);
		const j = i + dir;
		if (i < 0 || j < 0 || j >= arr.length) return;
		[arr[i], arr[j]] = [arr[j], arr[i]];
		currentFlow.steps = arr;
		markFlowDirty(currentFlow);
	};
	// [+ 새 스텝]: 빈 스텝 블록을 만들어 저장하고 현재 플로우에 추가(2콜).
	const createStepInFlow = () => {
		if (!currentFlow) return;
		const b = blankBlock();
		b.completion = { type: "objectives", objectives: [{ id: genObjId(), criterion: "" }], minCovered: null, maxTurns: 6, next: null };
		blocks = [...blocks, b];
		saveBlock(b, () => {
			if (b.id) { currentFlow.steps = [...currentFlow.steps, b.id]; saveFlow(currentFlow); }
		});
	};

	// ── ⑨ 코스(커리큘럼): 플로우(레슨)를 순서대로 묶은 상위 단위(CHAT_COURSES).
	//    한 레슨을 마치면 코스의 다음 레슨으로 이어진다(런타임 course-advance). 레슨 내용은 [플로우]에서 편집.
	let courses = [];
	let currentCourseIdx = 0;
	const loadCourses = () => {
		if (!chat?.chatId) return;
		PRConnect.send("/api/Chat/getCourseList", { chatId: chat.chatId }, (data) => {
			if (data.header.status === "success") {
				const list = (data.result || []).map((c) => ({ ...c, flows: c.flows || [], _dirty: false }));
				// 테마(채팅) 하나에 여러 코스 가능 — 비면 시작 코스 1개를 자동으로 둔다(저장은 편집 시).
				courses = list.length ? list : [{ ...blankCourse(), _dirty: false }];
			}
		});
	};
	$: currentCourse = courses[currentCourseIdx] ?? courses[0] ?? null;
	// [레슨 담기] 드롭다운: 저장된 레슨 유닛 중 아직 이 코스에 없는 것(평가 유닛은 [+ 평가 추가]로 생성).
	$: flowsNotInCourse = lessonFlows.filter((f) => f.id && !(currentCourse?.flows ?? []).includes(f.id));
	const flowLabel = (id) => { const f = flows.find((x) => x.id === id); return f ? (f.title || "제목 없는 레슨") : id; };
	// 코스 뷰의 레슨 카드에 그 레슨의 스텝 요약을 보여줌(구조가 보이게 — 참조 라벨만 X).
	const flowStepLabels = (flowId) => {
		const f = flows.find((x) => x.id === flowId);
		return (f?.steps ?? []).map((sid) => {
			const b = blocks.find((x) => x.id === sid);
			return b ? truncate(b.description || "(이름 없음)", 16) : sid;
		});
	};
	const markCourseDirty = (course) => { course._dirty = true; courses = courses; };
	const blankCourse = () => ({ id: null, chatId: chat.chatId, title: "", flows: [], sort: courses.length, _dirty: true });
	// 테마(채팅) 하나에 여러 코스 — 새 코스 추가.
	const addCourse = () => { courses = [...courses, blankCourse()]; currentCourseIdx = courses.length - 1; };
	const saveCourse = (course) => {
		const payload = { chatId: chat.chatId, title: course.title, flows: course.flows, sort: course.sort ?? 0 };
		const isCreate = !course.id;
		const cb = (data) => {
			if (data.header.status === "success") {
				if (isCreate && typeof data.result === "string") course.id = data.result;
				course._dirty = false;
				courses = courses;
			}
		};
		if (course.id) PRConnect.send("/api/Chat/modifyCourse", { ...payload, id: course.id }, cb);
		else PRConnect.send("/api/Chat/createCourse", payload, cb);
	};
	const deleteCourse = (course, idx) => {
		const done = () => {
			courses = courses.filter((_, i) => i !== idx);
			if (!courses.length) courses = [{ ...blankCourse(), _dirty: false }];   // 최소 하나 보장(비면 시작 코스)
			if (currentCourseIdx >= courses.length) currentCourseIdx = Math.max(0, courses.length - 1);
		};
		if (!course.id) { done(); return; }
		if (!confirm("이 코스를 삭제할까요? (레슨·블록 자체는 지워지지 않아요)")) return;
		PRConnect.send("/api/Chat/deleteCourse", { id: course.id }, (data) => { if (data.header.status === "success") done(); });
	};
	// 코스 안 레슨(플로우) 순서/구성(flowId 기준). 변경 후 saveCourse(currentCourse)로 저장.
	const addExistingFlowToCourse = (flowId) => {
		if (!flowId || !currentCourse) return;
		currentCourse.flows = [...currentCourse.flows, flowId];
		markCourseDirty(currentCourse);
	};
	const removeFlowFromCourse = (flowId) => {
		if (!currentCourse) return;
		currentCourse.flows = currentCourse.flows.filter((f) => f !== flowId);
		markCourseDirty(currentCourse);
	};
	const moveFlowInCourse = (flowId, dir) => {
		if (!currentCourse) return;
		const arr = currentCourse.flows;
		const i = arr.indexOf(flowId);
		const j = i + dir;
		if (i < 0 || j < 0 || j >= arr.length) return;
		[arr[i], arr[j]] = [arr[j], arr[i]];
		currentCourse.flows = arr;
		markCourseDirty(currentCourse);
	};
	// 레슨(플로우) 내용 편집으로 점프: [플로우] 뷰로 전환 + 그 레슨 선택(레슨 유닛 인덱스 기준).
	const editFlow = (flowId) => {
		const idx = lessonFlows.findIndex((f) => f.id === flowId);
		if (idx >= 0) { currentFlowIdx = idx; viewMode = "flows"; }
	};

	// ── ⑨ 코스 아이템 = 유닛(레슨|평가). bind: 위해 실제 객체 참조로 순회({@const} 회피).
	//    평가 유닛 = policy.kind='eval' flow + 그 안의 judge 스텝 1개(대화를 채점). 평가 저작은 여기(코스)에서.
	$: courseUnits = (currentCourse?.flows ?? []).map((flowId) => {
		const flow = flows.find((f) => f.id === flowId);
		const evalBlock = flow && isEvalUnit(flow) ? blocks.find((b) => b.id === (flow.steps ?? [])[0]) : null;
		return { flowId, flow, isEval: isEvalUnit(flow), evalBlock };
	});
	const markEvalDirty = (u) => { if (u.evalBlock) { u.evalBlock._dirty = true; blocks = blocks; } };
	// ── 스텝 저작 즉시 점검(stepLint): 블록 객체 → 지적 목록.
	//    markDirty/markEvalDirty가 `blocks = blocks`로 무효화하므로 타이핑하는 동안 바로 갱신된다.
	//    저장은 막지 않는다 — 어긋난 곳을 알려줄 뿐 판단은 저작자 몫.
	//    스텝 단위 점검 + 플로우 경계 점검(앞 스텝과 목표가 겹치는지 등)을 한 맵에 합쳐 담는다.
	$: lintMap = buildLintMap(blocks, flows, { guideStyle: chat?.guideStyle, inputKeys });
	// 레슨 계층 요약 — 스텝 카드가 접혀 있어도 "이 레슨에 볼 게 있다"는 신호는 위에서 보이게.
	$: currentFlowLint = currentFlow
		? summarizeLint((currentFlow.steps || []).map((id) => blocks.find((b) => b.id === id)).filter(Boolean), lintMap)
		: { errors: 0, warnings: 0, total: 0 };
	const evalDirty = (u) => !!(u.evalBlock?._dirty || u.flow?._dirty);
	const saveEvalUnit = (u) => { if (u.evalBlock) saveBlock(u.evalBlock); if (u.flow) saveFlow(u.flow); };
	// self 평가의 유도스텝(objectives) 편집 — 대화로 이끌어낼 항목들.
	const addEvalObjective = (u) => {
		if (!u.evalBlock) return;
		const c = u.evalBlock.completion;
		c.objectives = [...(c.objectives || []), { id: genObjId(), criterion: "" }];
		markEvalDirty(u);
	};
	const removeEvalObjective = (u, i) => {
		if (!u.evalBlock) return;
		const c = u.evalBlock.completion;
		c.objectives = (c.objectives || []).filter((_, idx) => idx !== i);
		markEvalDirty(u);
	};
	// 평가 범위 전환 시 필드 보강: self면 유도스텝 최소 1개 확보.
	const ensureEvalScope = (u) => {
		const c = u.evalBlock?.completion; if (!c) return;
		if (c.scope === "self" && (!Array.isArray(c.objectives) || !c.objectives.length))
			c.objectives = [{ id: genObjId(), criterion: "" }];
		if (c.scope === "self" && c.situation == null) c.situation = "";
		// AI 텍스트 슬롯은 어느 범위에서든 필요하다 — self면 '여는 말', 회고면 '평가문 지침'.
		if (!(u.evalBlock.contents || []).some(isAiText))
			u.evalBlock.contents = [{ type: "text", data: { mode: "ai", prompt: "" } }, ...(u.evalBlock.contents || [])];
		markEvalDirty(u);
	};
	// [+ 평가 추가]: 평가 유닛(judge 블록 스텝 → kind='eval' 플로우 → 코스에 추가) 3콜 체인.
	const createEvalUnit = () => {
		if (!currentCourse) return;
		const b = blankBlock();
		b.description = "종합 평가";
		// 평가문 지침이 들어갈 AI 텍스트 슬롯 1칸(= 이 블록이 내는 유일한 텍스트).
		b.contents = [{ type: "text", data: { mode: "ai", prompt: "" } }];
		// 형성평가(게이트 없음): 대화 없이 지난 대화를 근거로 평가문을 만드는 블록. 지침은 콘텐츠 AI 슬롯에 쓴다.
		//   (예전 scope='self'는 레거시 — "레슨 스텝 + 직전 레슨 평가" 조합으로 대체)
		b.completion = {
			type: "eval",
			scope: "previous",
			next: null,
		};
		blocks = [...blocks, b];
		saveBlock(b, () => {
			if (!b.id) return;
			const f = { id: null, chatId: chat.chatId, title: "평가", steps: [b.id], policy: { kind: "eval" }, sort: flows.length, _dirty: true };
			flows = [...flows, f];
			saveFlow(f, () => {
				if (f.id) { currentCourse.flows = [...currentCourse.flows, f.id]; saveCourse(currentCourse); }
			});
		});
	};

	// ── 지도: "주소로 가져오기" — 서버가 네이버 지오코딩+정적지도 이미지를 fetch(키는 서버에만),
	//    그 이미지를 기존 자산 업로드(createAsset)에 태워 assetId로 저장. 저작 시 1회만 호출. ──
	let mapFetchingId = null; // 로딩 중인 content 식별(객체 동일성)
	const base64ToFile = (b64, mime, name) => {
		const bin = atob(b64);
		const arr = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
		return new File([arr], name, { type: mime || "image/png" });
	};
	const fetchMapForContent = (block, content) => {
		const address = (content.data.address || "").trim();
		const lat = String(content.data.lat ?? "").trim();
		const lng = String(content.data.lng ?? "").trim();
		if (!address && !(lat && lng)) {
			alert("주소 또는 좌표(위도/경도)를 입력해 주세요.");
			return;
		}
		mapFetchingId = content;
		blocks = blocks;
		PRConnect.send("/api/Chat/fetchMapImage", { address, lat, lng }, async (data) => {
			try {
				if (data.header.status !== "success") {
					alert(data.header.message || "지도 이미지를 가져오지 못했어요.");
					return;
				}
				const { lat, lng, base64, mimeType } = data.result || {};
				const ext = (mimeType || "").includes("png") ? "png" : "jpg";
				const file = base64ToFile(base64, mimeType, `map-${Date.now()}.${ext}`);
				const asset = await createAsset(chat.chatId, file, "maps");
				content.data.assetId = asset.id;
				content.data.imageUrl = asset.url;
				content.data.lat = lat;
				content.data.lng = lng;
				markDirty(block);
			} catch (e) {
				alert("지도 이미지를 저장하지 못했어요: " + (e?.message || e));
			} finally {
				mapFetchingId = null;
				blocks = blocks;
			}
		});
	};

	// ── 이미지: 자산(assetId, ref) 선택 — 공용 ImagePicker 모달 사용 ──
	// 콘텐츠는 assetId를 저장(+미리보기용 url 캐시). 서버가 서빙 시 current 버전 URL로 resolve.
	// key: image='url' / card='imageUrl' (캐시 필드명). assetId는 항상 data.assetId.
	let pickerOpen = false;
	let pickerMode = "single";
	let pickerCurrent = null;
	let pickerExisting = [];
	let pickerSelect = null; // ({assetId,url,name}) => void
	let pickerAdd = null; // ({items}) => void

	const openImagePicker = (block, content, key) => {
		pickerMode = "single";
		pickerCurrent = content.data.assetId || null;
		pickerExisting = [];
		pickerAdd = null;
		pickerSelect = ({ assetId, url }) => {
			content.data = { ...content.data, assetId, [key]: url };
			markDirty(block);
		};
		pickerOpen = true;
	};
	const clearImageAsset = (block, content, key) => {
		const { assetId, ...rest } = content.data;
		content.data = { ...rest, [key]: "" };
		markDirty(block);
	};

	// 갤러리: content.data.images[] = [{assetId, url(캐시), alt}]. 라이브러리에서 여러 장 추가(append).
	const openGalleryPicker = (block, content) => {
		pickerMode = "multi";
		pickerCurrent = null;
		pickerExisting = (content.data.images ?? []).map((i) => i.assetId).filter(Boolean);
		pickerSelect = null;
		pickerAdd = ({ items }) => {
			content.data = {
				...content.data,
				images: [
					...(content.data.images ?? []),
					...items.map((it) => ({ assetId: it.assetId, url: it.url, alt: "" })),
				],
			};
			markDirty(block);
		};
		pickerOpen = true;
	};
	const onPickerSelect = (e) => pickerSelect && pickerSelect(e.detail);
	const onPickerAdd = (e) => pickerAdd && pickerAdd(e.detail);
	const onPickerClose = () => {
		// 교체/롤백/업로드로 current 버전이 바뀌었을 수 있으니 미리보기 맵 새로고침
		loadAssetMap();
	};

	const removeGalleryImage = (block, content, i) => {
		content.data = {
			...content.data,
			images: (content.data.images ?? []).filter((_, idx) => idx !== i),
		};
		markDirty(block);
	};
	const moveGalleryImage = (block, content, i, dir) => {
		const images = [...(content.data.images ?? [])];
		const j = i + dir;
		if (j < 0 || j >= images.length) return;
		[images[i], images[j]] = [images[j], images[i]];
		content.data = { ...content.data, images };
		markDirty(block);
	};
	// 캐러셀 표시 방식: single(한 장씩) / multi(≈3장) / peek(양옆 걸치기) / grid(격자). 아이콘으로 선택.
	const setGalleryVariant = (block, content, variant) => {
		content.data = { ...content.data, variant };
		markDirty(block);
	};

	const fetchOg = (block, content) => {
		const url = (content.data?.url || "").trim();
		if (!url) return;
		PRConnect.send("/api/Chat/getLinkPreview", { url }, (data) => {
			if (data.header.status === "success" && data.result) {
				const r = data.result;
				content.data = {
					...content.data,
					url: r.url || url,
					title: r.title || content.data.title || "",
					body: r.body || content.data.body || "",
					imageUrl: r.imageUrl || content.data.imageUrl || "",
				};
				markDirty(block);
			}
		});
	};
	const moveContent = (block, i, dir) => {
		const j = i + dir;
		if (j < 0 || j >= block.contents.length) return;
		const arr = [...block.contents];
		[arr[i], arr[j]] = [arr[j], arr[i]];
		block.contents = arr;
		markDirty(block);
	};

	// 버튼
	// 기본은 followup(연관질문): '추가 + 라벨'만으로 바로 동작하는 빠른답장 버튼.
	// navigate(블록 이동)는 대상 블록이 있어야 의미가 있어 사용자가 타입을 바꿔서 씀.
	const addButton = (block) => {
		block.buttons = [
			...block.buttons,
			{ type: "followup", label: "", targetBlockId: "", text: "" },
		];
		markDirty(block);
	};
	const removeButton = (block, i) => {
		block.buttons = block.buttons.filter((_, idx) => idx !== i);
		markDirty(block);
	};

	// 저장 직전 정리:
	//  - followup: 텍스트를 비우면 라벨을 그대로 질문으로 사용(빠른답장). → 라벨만 있어도 저장됨.
	//  - navigate: 대상 블록(targetBlockId)이 없으면 동작 불가라 저장에서 제외(빈 대상 = 깨진 버튼).
	// ⚠️ 라벨이 아예 없는 버튼만 최종적으로 버린다.
	// ⭐ 저장에서 빠질 버튼인지 — 화면 경고와 cleanButtons 의 필터가 **같은 규칙**을 봐야 한다.
	//    (예전엔 필터만 있고 경고가 없어서, 라벨을 안 채운 버튼이 화면엔 남고 DB엔 안 들어갔다.
	//     저작자에겐 "만들었는데 리프레시하면 사라진다"로만 보인다 — 실제로 그렇게 잃었다)
	const buttonDropReason = (b) =>
		!b?.label?.trim()
			? "라벨을 입력해야 저장돼요"
			: b.type === "navigate" && !b.targetBlockId
				? "대상 블록을 선택해야 저장돼요"
				: null;

	// ⚠️ 필터는 buttonDropReason 하나로 판정한다. 여기서 조건을 따로 쓰면 화면 경고와 어긋나
	//    "경고는 없는데 저장은 안 되는" 버튼이 다시 생긴다.
	const cleanButtons = (buttons) =>
		(buttons || [])
			.filter((b) => !buttonDropReason(b))
			.map((b) =>
				b.type === "navigate"
					? {
							type: "navigate",
							label: b.label.trim(),
							targetBlockId: b.targetBlockId,
						}
					: {
							type: "followup",
							label: b.label.trim(),
							text: (b.text || "").trim() || b.label.trim(),
						},
			);
	// onDone: 저장 성공 후 콜백(선택). 겹침 재점검(loadOverlaps)은 매 저장마다 자동으로 하지 않고
	// (findBlockOverlaps=LLM 호출이라 잦으면 낭비), 겹침을 직접 해소하는 액션에서만 onDone으로 1회 호출한다.
	// 숫자 칸을 비우면 Svelte 가 null 을 넣는다. 그대로 저장되면 서버가 "중단 1 · 워밍업 0"으로 읽어
	// 첫 미달에 즉시 끝나는 테스트가 된다(실제로 그렇게 바뀌어 5문항 만에 끝났다). 저장 직전에 되돌린다.
	const normalizeProbe = (block) => {
		const c = block?.completion;
		// 사전 테스트를 가리키는 판정 블록(sameAs)은 자기 규칙이 없다 — 기본값을 박으면 원본과 다른 값처럼 보인다
		if (c?.type !== "probe" || c.sameAs) return;
		if (!(Number(c.stopAfter) > 0)) c.stopAfter = 3;
		if (!(Number(c.warmup) >= 0)) c.warmup = 0;
		c.stopAfter = Number(c.stopAfter);
		c.warmup = Number(c.warmup);
		(c.questions || []).forEach((q, i) => { if (!(Number(q.level) > 0)) q.level = i + 1; });
	};

	const saveBlock = (block, onDone) => {
		normalizeProbe(block);
		const savedButtons = cleanButtons(block.buttons);
		// 이번 저장에서 빠진 버튼 수 — 카드에 그대로 알린다. 화면엔 남고 DB엔 없으면
		// 저작자는 리프레시 전까지 알 방법이 없다(그게 이 값이 생긴 이유).
		block._buttonsDropped = (block.buttons || []).length - savedButtons.length;
		const payload = {
			chatId: chat.chatId,
			description: block.description,
			contents: block.contents,
			terminal: !!block.terminal,
			buttons: savedButtons,
			// 폼은 콘텐츠 항목(contents 안 {type:'form'})으로 저장한다 → FIELDS 컬럼은 비운다.
			// (레거시 블록은 로드 시 form 콘텐츠로 변환됨. 저장하면 CONTENTS로 동결되고 FIELDS는 null.)
			fields: null,
			completion: block.completion ?? null, // 완료조건(스텝, ⑨) — 없으면 null=일반 블록
			// 역할(설정 완료 화면)도 함께 저장한다. 예전엔 별도 토글 API 로만 붙였는데,
			// 저장 때 payload 에 없어 서버가 NULL 로 덮어 리프레시하면 "사라진 것처럼" 보였다.
			role: block.role ?? null,
			sort: block.sort ?? 0,
		};
		// 신규 생성일 때만 서버가 준 새 id를 채운다.
		// modifyBlock은 결과로 빈 객체({})를 반환하는데, 이를 block.id에 덮어쓰면
		// 다음 저장이 WHERE ID={} 로 나가 0건 업데이트(= 조용한 저장 실패)가 된다.
		const isCreate = !block.id;
		const cb = (data) => {
			if (data.header.status === "success") {
				if (isCreate && typeof data.result === "string") block.id = data.result;
				block._dirty = false;
				blocks = blocks;
				// 판정 스텝이면 결과 키를 프로필 항목으로 자동 선언한다(안 하면 값이 안 남는다).
				syncProbeKeys(block);
				if (onDone) onDone();
			}
		};
		if (block.id)
			PRConnect.send(
				"/api/Chat/modifyBlock",
				{ ...payload, id: block.id },
				cb,
			);
		else PRConnect.send("/api/Chat/createBlock", payload, cb);
	};

	// 블록 바깥으로 포커스가 완전히 나가면(다른 블록/빈 영역 클릭) 변경분 자동 저장.
	// relatedTarget이 같은 블록 안이면(필드 간 이동) 저장하지 않음.
	const autoSaveOnBlur = (block, e) => {
		if (e.currentTarget.contains(e.relatedTarget)) return;
		clearPreview(); // 블록 밖으로 포커스가 나가면 프리뷰 종료 → 대화로 복귀
		if (!block._dirty) return;
		// 내용 없는 신규 블록이 클릭아웃만으로 생성되는 것 방지
		if (!block.id && !block.description?.trim() && block.contents.length === 0)
			return;
		saveBlock(block);
	};

	// ⚠️ 신원 기반 삭제(인덱스 X). 플로우 뷰에선 인덱스가 플로우 위치라 인덱스 삭제 시 엉뚱한 블록이 지워졌음.
	const deleteBlock = (block) => {
		if (!block.id) {
			blocks = blocks.filter((b) => b !== block);
			return;
		}
		// 플로우에 물린 블록이면 경고(삭제 시 그 플로우에서도 빠짐 → 댕글링 참조 방지 위해 함께 정리).
		const usedIn = flows.filter((f) => (f.steps || []).includes(block.id));
		const msg = usedIn.length
			? `이 블록은 플로우 "${usedIn.map((f) => f.title || "제목 없음").join('", "')}"의 스텝이에요.\n삭제하면 그 플로우에서도 빠집니다. 정말 삭제할까요?`
			: "이 블록을 삭제할까요?";
		if (!confirm(msg)) return;
		PRConnect.send("/api/Chat/deleteBlock", { id: block.id }, (data) => {
			if (data.header.status === "success") {
				blocks = blocks.filter((b) => b !== block);
				// 플로우 steps에서 죽은 id 제거 + 저장(참조 정합성)
				usedIn.forEach((f) => { f.steps = (f.steps || []).filter((s) => s !== block.id); saveFlow(f); });
			}
		});
	};

	$: targetOptions = blocks.filter((b) => b.id);
	const labelOf = (type) => schemas[type]?.label ?? type;
	// 긴 블록명이 대상 드롭다운을 부풀리지 않게 옵션 표시 텍스트를 …로 자른다.
	const truncate = (s, n = 12) => {
		s = (s || "").trim();
		return s.length > n ? s.slice(0, n) + "…" : s;
	};
</script>

{#if chat.isCurrentTab(tab)}
	<div class="chat-contents-wrap first block-tab">
		<!-- 챗 전체 설정(용도·학습자 정보·수집 항목·유도 방식·쓰는 말/받아 주기)은 [대화 설정] 탭으로 옮겼다 —
		     이 탭은 콘텐츠만. 테스트 챗이면 아래 화면이 판정 기준·완료 화면으로 바뀌므로 그 사실만 한 줄로 알린다. -->
		{#if isTestChat}
			<div class="settings-bar">
				<span>
					<b>{chat.purpose === "exit" ? "사후 테스트" : "사전 테스트"}</b> 채팅 — 아래에서 <b>판정 기준</b>과 <b>완료 화면</b>만 정하면 됩니다.
					결과는 프로필에 남아 다른 채팅들의 대화를 바꿔요.
				</span>
				<button type="button" class="mini" on:click={goSettings}>용도 바꾸기</button>
			</div>
		{/if}

		{#if !isTestChat}
		<!-- 뷰 전환: 자유 블록(FAQ) / 플로우(레슨=스텝 묶음) / 코스(레슨 묶음) -->
		<div class="view-seg">
			<button
				type="button"
				class:active={viewMode === "blocks"}
				on:click={() => (viewMode = "blocks")}>자유 블록</button
			>
			<button
				type="button"
				class:active={viewMode === "flows"}
				on:click={() => (viewMode = "flows")}>플로우(레슨)</button
			>
			<button
				type="button"
				class:active={viewMode === "courses"}
				on:click={() => (viewMode = "courses")}>코스</button
			>
		</div>
		{/if}
		{#if viewMode !== "blocks" && !isTestChat}
		<!-- 챗 기본값 요약 — 스텝 카드의 덮어쓰기가 무엇을 덮는지 보이게. 바꾸는 곳은 [대화 설정] 탭. -->
		<div class="settings-bar">
			<span class="sb-label">챗 기본값</span>
			<span>유도 방식 <b>{guideStyleLabel(chat.guideStyle)}</b></span>
			<span>쓰는 말·받아 주기 <b>{replySummary(chat.replyStyle) || "지정 안 함"}</b></span>
			<button type="button" class="mini" on:click={goSettings}>대화 설정에서 바꾸기</button>
		</div>
		{/if}
		{#if viewMode === "courses"}
			<div class="entry-hint">
				📚 <b>코스</b> = 플로우(레슨)를 순서대로 묶은 커리큘럼. 한 레슨을 마치면
				<b>자동으로 다음 레슨</b>으로 이어져요. 진행바에 "레슨 N/M"이 뜹니다.
				레슨 <b>내용(스텝)</b>은 각 레슨 카드의 <b>[편집]</b>이나 [플로우] 탭에서 고쳐요.
			</div>
			<!-- 테마(채팅) 하나에 여러 코스(시나리오 시퀀스) — 코스 탭 + [+ 새 코스] -->
			<div class="flow-tabs">
				{#each courses as c, ci}
					<button
						type="button"
						class:active={ci === currentCourseIdx}
						on:click={() => (currentCourseIdx = ci)}
						>{c.title || "코스 " + (ci + 1)}{c._dirty ? " *" : ""}</button
					>
				{/each}
				<button type="button" class="flow-tab-add" on:click={addCourse}
					>+ 새 코스</button
				>
			</div>
			{#if currentCourse}
				<div class="flow-head">
					<input
						class="text-field flow-title"
						type="text"
						placeholder="코스 제목 — 예: 기초 영어 회화"
						bind:value={currentCourse.title}
						on:input={() => markCourseDirty(currentCourse)}
					/>
					<button
						class="save"
						class:has-changes={currentCourse._dirty}
						disabled={!currentCourse._dirty}
						on:click={() => saveCourse(currentCourse)}
						>{currentCourse.id ? "저장" : "생성"}</button
					>
					<button
						class="del"
						on:click={() => deleteCourse(currentCourse, currentCourseIdx)}
						>코스 삭제</button
					>
				</div>
				{#if !currentCourse.flows.length}
					<div class="flow-empty">
						아직 레슨이 없어요. 맨 아래 [레슨 담기]로 플로우를 넣어보세요.
					</div>
				{/if}
				{#each courseUnits as u, li (u.flowId)}
					<div class="course-unit" class:eval={u.isEval}>
						<div class="flow-step-head">
							<span class="flow-step-no">{li + 1}</span>
							<span class="unit-kind" class:eval={u.isEval}>{u.isEval ? "평가" : "레슨"}</span>
							<span class="flow-step-title">{u.flow ? (u.flow.title || (u.isEval ? "평가" : "제목 없는 레슨")) : u.flowId}</span>
							<span class="head-spacer" />
							<button type="button" class="mini" on:click={() => moveFlowInCourse(u.flowId, -1)} disabled={li === 0}>↑</button>
							<button type="button" class="mini" on:click={() => moveFlowInCourse(u.flowId, 1)} disabled={li === courseUnits.length - 1}>↓</button>
							{#if !u.isEval}
								<button
									type="button"
									class="mini preview-from"
									disabled={!u.flow?.id || !lessonFirstStep(u.flow) || lessonUnsaved(u.flow, blocks)}
									title={previewFromTitle(u.flow, blocks)}
									on:click={() => previewFromLesson(u.flow)}>▶ 프리뷰</button
								>
								<button type="button" class="mini" on:click={() => editFlow(u.flowId)}>편집</button>
							{/if}
							<button type="button" class="mini del" on:click={() => removeFlowFromCourse(u.flowId)}>코스에서 빼기</button>
						</div>
						{#if u.isEval}
							{#if u.evalBlock}
								<div class="eval-editor">
									<div class="eval-note">🧪 평가는 <b>블록처럼</b> 대화·회고로 <b>평가문</b>을 만들어요(점수·합불 없는 형성평가). 끝나면 다음 유닛 있으면 <b>[이어서 학습하기]</b>, 없으면 <b>[종료하기]</b>가 나와요.</div>
									<input class="text-field" type="text" placeholder="평가 이름 — 예: 종합 평가" bind:value={u.flow.title} on:input={() => { if (u.evalBlock) { u.evalBlock.description = u.flow.title; markEvalDirty(u); } markFlowDirty(u.flow); }} />
									<label class="eval-field">평가 범위
										<Select bind:value={u.evalBlock.completion.scope} on:change={() => ensureEvalScope(u)}>
											<option value="previous">직전 레슨 — 바로 앞 레슨의 대화를 평가</option>
											<option value="course">전체 여정 — 지금까지의 대화를 평가</option>
											<!-- self(평가 안에서 직접 대화)는 레거시. "레슨 스텝 + 직전 레슨 평가" 조합이
											     같은 일을 기존 재료로 하므로 새로 고를 수 없게 두고, 이미 self인 유닛에서만 보인다. -->
											{#if u.evalBlock.completion.scope === "self"}
												<option value="self">self — 이 평가 안에서 대화로 확인 (예전 방식)</option>
											{/if}
										</Select>
									</label>
									{#if u.evalBlock.completion.scope === "self"}
										<div class="eval-hint">이 평가는 <b>예전 방식</b>이에요. 지금은 같은 일을 <b>레슨 스텝 + 직전 레슨 평가</b>로 나눠서 만들어요 — 대화는 레슨이, 평가문은 평가 유닛이 맡습니다. 그대로 둬도 동작해요.</div>
										<!-- self 평가는 유도 스텝과 같은 구조(EvalCompletion이 ObjectivesCompletion에 위임) →
										     칸도 스텝 카드와 똑같이 [여는 말=블록 콘텐츠] + [이 평가의 상황=completion.situation]. -->
										{#if u.evalBlock.contents?.[0]?.data}
											<label class="eval-field">여는 말 <span class="opt-tag">처음 한 번</span>
												<textarea class="text-field" rows="2" placeholder="이 평가를 열 때 나가는 말 — 예: 오늘 배운 걸 새 친구에게 써 보자고 알려준 뒤, 새 친구가 된 것처럼 영어로 인사를 건넨다." bind:value={u.evalBlock.contents[0].data.prompt} use:autoGrow={u.evalBlock.contents[0].data.prompt} on:input={() => markEvalDirty(u)} />
											</label>
										{/if}
										<label class="eval-field">이 평가의 상황
											<textarea class="text-field" rows="3" placeholder="이 평가에서 벌어지는 일 — 예: 처음 만난 친구처럼 가볍게 이어간다. 시험 분위기를 내지 말고, 한 번에 한 가지만 묻고, 학습자가 스스로 말하게 둔다." bind:value={u.evalBlock.completion.situation} use:autoGrow={u.evalBlock.completion.situation} on:input={() => markEvalDirty(u)} />
											<div class="eval-hint">챗 세계관 위에, <b>이 평가에서만</b> 달라지는 무대·역할·흐름을 적어요 — 대화 내내 참고합니다</div>
										</label>
										<div class="eval-field">유도할 항목 (대화로 이끌어낼 것)
											{#each u.evalBlock.completion.objectives || [] as obj (obj.id)}
												<div class="block-row inline">
													<input class="text-field" type="text" placeholder="예: 영어로 인사한다" bind:value={obj.criterion} on:input={() => markEvalDirty(u)} />
													<button type="button" class="mini del" on:click={() => removeEvalObjective(u, (u.evalBlock.completion.objectives || []).indexOf(obj))}>삭제</button>
												</div>
											{/each}
											<button type="button" class="mini add" on:click={() => addEvalObjective(u)}>+ 항목 추가</button>
										</div>
										<label class="eval-field">유도문형
											<Select value={u.evalBlock.completion.initiative || ""} on:change={(e) => { setInitiative(u.evalBlock.completion, e.target.value); markEvalDirty(u); }}>
												{#each INITIATIVE_OPTIONS as opt}
													<option value={opt.key}>{opt.label}</option>
												{/each}
											</Select>
										</label>
										<label class="eval-field">유도 방식
											<Select value={u.evalBlock.completion.guideStyle?.preset || ""} on:change={(e) => { setGuideStylePreset(u.evalBlock.completion, e.target.value); markEvalDirty(u); }}>
												{#each GUIDE_STYLE_OPTIONS as opt}
													<option value={opt.key}>{opt.label}</option>
												{/each}
											</Select>
										</label>
										{#if u.evalBlock.completion.guideStyle?.preset === "custom"}
											<textarea class="text-field" rows="3" placeholder="이 평가에서 AI가 어떻게 이끌지 직접 작성" bind:value={u.evalBlock.completion.guideStyle.custom} use:autoGrow={u.evalBlock.completion.guideStyle.custom} on:input={() => markEvalDirty(u)} />
										{/if}
										<label class="eval-field">평가 기준 (평가문을 쓰는 관점)
											<textarea class="text-field" rows="2" placeholder="무엇을 잘했는지 볼지 — 예: 배운 인사·자기소개·주문을 자연스럽고 정확하게 했는가" bind:value={u.evalBlock.completion.criteria} use:autoGrow={u.evalBlock.completion.criteria} on:input={() => markEvalDirty(u)} />
										</label>
									{:else}
										<div class="eval-hint">이 평가는 <b>대화 없이</b> {u.evalBlock.completion.scope === "course" ? "지금까지의 전체 대화" : "직전 레슨의 대화"}를 보고 <b>평가문</b>을 만들어 보여줘요. 그 평가문이 이 블록이 내는 유일한 텍스트라, 지침도 다른 블록처럼 아래 칸에 씁니다.</div>
										<!-- 평가도 블록이라 화면을 구성할 수 있다 — 블록 카드와 **같은** 콘텐츠 편집기.
										     첫 AI 텍스트 슬롯이 평가문 자리이고, 그 옆에 이미지·고정 문구를 얹을 수 있다. -->
										<div class="eval-field">
											<span class="eval-field-label">평가 화면</span>
											<BlockContentsEditor
												block={u.evalBlock}
												{schemas}
												{assetMap}
												chatId={chat.chatId}
												firstSlotNote="이 칸이 평가문 자리예요 — 지난 대화를 어떤 관점으로 돌아볼지 적으면, 그 지침대로 쓴 평가문이 여기 채워집니다."
												on:change={() => markEvalDirty(u)}
												on:pickImage={(e) => openImagePicker(u.evalBlock, e.detail.content, e.detail.key)}
												on:pickGallery={(e) => openGalleryPicker(u.evalBlock, e.detail.content)}
											/>
										</div>
									{/if}
									{#if lintMap.get(u.evalBlock)?.length}
										<div class="step-lint">
											{#each lintMap.get(u.evalBlock) as r}
												<div class="lint-row" class:err={r.level === "error"}>
													<span class="lint-icon">{r.level === "error" ? "✕" : "⚠"}</span>
													<div>
														<div class="lint-msg">{r.message}</div>
														<div class="lint-why">{r.why}</div>
													</div>
												</div>
											{/each}
										</div>
									{/if}
									<div class="eval-row">
										<button class="save" class:has-changes={evalDirty(u)} disabled={!evalDirty(u)} on:click={() => saveEvalUnit(u)}>저장</button>
									</div>
								</div>
							{:else}
								<span class="course-step-empty">평가 블록을 찾을 수 없어요.</span>
							{/if}
						{:else}
							<div class="course-lesson-steps">
								{#each flowStepLabels(u.flowId) as sl}
									<span class="course-step-chip">{sl}</span>
								{:else}
									<span class="course-step-empty">(스텝 없음 — [편집]에서 추가)</span>
								{/each}
							</div>
						{/if}
					</div>
				{/each}
				<div class="add-step-bar">
					<Select
						class="tw-flex-1"
						bind:value={pickFlowForCourse}
						on:change={() => {
							if (!pickFlowForCourse) return;
							addExistingFlowToCourse(pickFlowForCourse);
							pickFlowForCourse = "";
						}}
					>
						<option value="">기존 레슨(플로우) 담기…</option>
						{#each flowsNotInCourse as f}
							<option value={f.id}>{f.title || "제목 없는 레슨"}</option>
						{/each}
					</Select>
					<button class="add-block flow-add-step" on:click={createEvalUnit}>+ 평가 추가</button>
				</div>
			{:else}
				<div class="flow-empty">
					코스를 불러오는 중…
				</div>
			{/if}
		{:else if viewMode === "flows"}
			<div class="entry-hint">
				🎓 <b>플로우</b> = 스텝(유도 목표를 가진 블록)을 순서대로 묶은 레슨. 아래에서
				각 스텝이 <b>블록 카드 그대로</b> 편집돼요(스텝 = 블록을 감싸는 껍데기). 진행도가
				저장돼 리셋해도 이어서 할 수 있어요.
			</div>
			<div class="flow-tabs">
				{#each lessonFlows as f, fi}
					<button
						type="button"
						class:active={fi === currentFlowIdx}
						on:click={() => (currentFlowIdx = fi)}
						>{f.title || "레슨 " + (fi + 1)}{f._dirty ? " *" : ""}</button
					>
				{/each}
				<button type="button" class="flow-tab-add" on:click={addFlow}
					>+ 새 플로우</button
				>
			</div>
			{#if currentFlow}
				<div class="flow-head">
					<input
						class="text-field flow-title"
						type="text"
						placeholder="플로우(레슨) 제목 — 예: 레슨 1. 인사"
						bind:value={currentFlow.title}
						on:input={() => markFlowDirty(currentFlow)}
					/>
					<button
						class="save"
						class:has-changes={currentFlow._dirty}
						disabled={!currentFlow._dirty}
						on:click={() => saveFlow(currentFlow)}
						>{currentFlow.id ? "저장" : "생성"}</button
					>
					<button
						type="button"
						class="mini preview-from"
						disabled={!currentFlow.id || !lessonFirstStep(currentFlow) || lessonUnsaved(currentFlow, blocks)}
						title={previewFromTitle(currentFlow, blocks)}
						on:click={() => previewFromLesson(currentFlow)}>▶ 프리뷰에서 이 레슨부터</button
					>
					<button
						class="del"
						on:click={() => deleteFlow(currentFlow)}
						>플로우 삭제</button
					>
				</div>
				{#if !currentFlow.steps.length}
					<div class="flow-empty">
						아직 스텝이 없어요. 맨 아래 [스텝 추가]로 넣어보세요.
					</div>
				{:else if currentFlowLint.total}
					<div class="flow-lint" class:err={currentFlowLint.errors > 0}>
						이 레슨의 스텝에 점검할 곳이 {currentFlowLint.total}건 있어요{currentFlowLint.errors
							? ` — 그중 ${currentFlowLint.errors}건은 스텝이 제대로 진행되지 않는 문제예요`
							: ""}. 아래 스텝 카드에서 확인해 주세요.
					</div>
				{/if}
			{:else}
				<div class="flow-empty">
					플로우가 없어요. [+ 새 플로우]로 레슨을 만들어보세요.
				</div>
			{/if}
		{:else}
		<div class="tw-flex tw-items-center tw-gap-2">
			<div class="tab-text">대화 블록을 만들어주세요!</div>
			<span class="tw-flex-1" />
			<Button
				variant="outline"
				size="sm"
				title="예상 질문을 자동으로 만들어 여러 개를 한 번에 점검"
				on:click={() => (showTestSheet = true)}>🤖 여러 질문 한번에 점검</Button
			>
		</div>
		{#if !isTestChat}
			<div class="entry-hint">
				🚩 <b>첫 대화 블록</b>으로 지정한 블록부터 대화가 시작돼요. 지정하지 않으면 맨 위 블록이 쓰입니다.
			</div>
		{/if}

		<!-- 중요한 흐름은 버튼으로 유도: AI 추측 없이 확실한 길 -->
		<div class="tw-mb-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-p-3 tw-font-sans tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
			💡 <b class="tw-text-foreground">중요한 흐름은 버튼으로 이어주세요.</b> 블록 아래
			<b class="tw-text-foreground">[블록 이동]</b> 버튼은 AI 추측 없이 정확히 그 블록으로 넘어가요.
			고객이 자유롭게 물어보는 말은 아래 '이럴 때 보여주세요' 설명을 보고 AI가 알아서 골라요.
		</div>

		<!-- 겹침 점검(수동): findBlockOverlaps=LLM 호출이라 저장마다 자동 X. 저작 마친 뒤 눌러서 갱신 -->
		<div class="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-font-sans">
			<Button variant="outline" size="sm" on:click={loadOverlaps} disabled={overlapLoading}>
				{overlapLoading ? "겹침 점검 중… (몇 초 걸려요)" : "🔍 블록 겹침 점검"}
			</Button>
			{#if !overlaps.length && !overlapLoading}
				<span class="tw-text-xs tw-text-muted-foreground">헷갈리는 블록 쌍이 없어요</span>
			{/if}
		</div>

		<!-- 블록 겹침 경고: 섞일 위험이 있는 쌍을 알려줌 -->
		{#if overlaps.length}
			<div class="tw-mb-3 tw-space-y-2 tw-font-sans">
				{#each overlaps as ov}
					<div class="tw-rounded-lg tw-border tw-border-destructive/40 tw-bg-destructive/5 tw-p-3 tw-text-xs tw-leading-relaxed tw-text-foreground">
						<div>
							⚠️ <b>"{ov.aDescription || "(설명 없음)"}"</b> 와
							<b>"{ov.bDescription || "(설명 없음)"}"</b> 블록이 헷갈릴 수 있어요.
							{#if ov.reason}<span class="tw-text-muted-foreground">{ov.reason}</span>{/if}
						</div>
						{#if ov.ambiguousExample}
							<div class="tw-mt-1 tw-text-muted-foreground">
								예: <span class="tw-font-medium tw-text-foreground">"{ov.ambiguousExample}"</span> 처럼 물으면 둘 다 걸려요.
							</div>
						{/if}
						<div class="tw-mt-1 tw-text-muted-foreground">
							💡 단어만 바꾸면 또 겹칠 수 있어요. <b class="tw-text-foreground">되묻는 블록</b>을 만들어 고객이 직접 고르게 하면 확실해요.
						</div>
						<div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
							<Button
								variant="default"
								size="sm"
								disabled={hubCreating[ov.aId + "|" + ov.bId]}
								on:click={() => createHubForOverlap(ov)}
							>
								{hubCreating[ov.aId + "|" + ov.bId] ? "만드는 중… (몇 초 걸려요)" : "🔀 되묻는 블록 만들기"}
							</Button>
							{#if ov.aRewrite && ov.bRewrite}
								<Button
									variant="outline"
									size="sm"
									disabled={hubCreating[ov.aId + "|" + ov.bId]}
									on:click={() => applyOverlapFix(ov)}
								>
									✏️ 설명만 바꾸기
								</Button>
							{/if}
						</div>
					</div>
				{/each}
			</div>
		{/if}

		<!-- 라우팅 테스트 도구: 우측 Sheet로 분리 (블록 화면은 위 🧪 아이콘으로 열기) -->
		<Sheet open={showTestSheet} title="🤖 예상 질문 일괄 점검" on:close={() => (showTestSheet = false)}>
			<div class="tw-space-y-3 tw-font-sans">
				<p class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
					예상 질문을 자동으로 만들어 한꺼번에 돌려보고, 블록끼리 헷갈리는(엉뚱한 블록·상관없는데 나옴) 곳을 찾아줘요.
					<br />한 문장씩 확인하려면 오른쪽 미리보기에서 <b class="tw-text-foreground">🔎 테스트 모드</b>를 켜고 직접 말 걸어보세요.
				</p>

				<!-- 예상 질문 여러 개를 자동으로 만들어 한꺼번에 점검 -->
			<Button
				variant="outline"
				size="sm"
				class="tw-w-full"
				on:click={runProbes}
				disabled={probeLoading}
			>
				{probeLoading ? "질문 만들어 점검 중… (몇 초 걸려요)" : "🤖 예상 질문 여러 개로 한 번에 점검"}
			</Button>

			{#if probeReport}
				<div class="tw-space-y-2">
					<div class="tw-flex tw-flex-wrap tw-gap-1.5">
						<Badge variant="secondary">✅ 잘 나와요 {probeReport.summary.hit}</Badge>
						<Badge variant="required">⚠️ 엉뚱한 블록 {probeReport.summary.collision}</Badge>
						<Badge variant="optional">⚪ 안 나와요 {probeReport.summary.miss}</Badge>
						<Badge variant="secondary">🆓 자유 답변 {probeReport.summary.ok_open}</Badge>
						<Badge variant="required">⚠️ 상관없는데 나옴 {probeReport.summary.leak}</Badge>
					</div>

					<div class="tw-space-y-1">
						{#each probeReport.probes as p}
							<div
								class="tw-rounded-lg tw-border tw-p-2 {p.outcome === 'collision' ||
								p.outcome === 'leak'
									? 'tw-border-destructive/40 tw-bg-destructive/5'
									: 'tw-border-border tw-bg-muted/30'}"
							>
								<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
									<Badge variant={badgeVariant(p.outcome)}>{outcomeIcon(p.outcome)} {outcomeLabel(p.outcome)}</Badge>
									<span class="tw-text-xs tw-font-medium tw-text-foreground">"{p.utterance}"</span>
								</div>
								<div class="tw-mt-1 tw-pl-0.5 tw-text-xs tw-text-muted-foreground">
									{#if p.intendedBlockId}기대: {p.intendedDescription || "(설명 없음)"}{:else}기대: 아무 블록과도 상관없는 질문{/if}
									→ 실제: {p.gotBlockId ? p.gotDescription || "(설명 없음)" : "자유 답변"}
								</div>
							</div>
						{/each}
					</div>

					<p class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
						⚠️ <b class="tw-text-foreground">엉뚱한 블록 · 상관없는데 나옴</b>이 진짜 봐야 할 신호예요 — 그 블록들의 '이럴 때 보여주세요' 설명을 서로 구분되게 다듬어 보세요. (자동으로 만든 질문이라 '잘 나와요'는 참고만)
					</p>
				</div>
			{/if}
			</div>
		</Sheet>

		<!-- 배지 [바로잡기] 피드백: 오라우팅된 질문을 올바른 블록의 예시로 추가 -->
		<Sheet open={!!feedback} title="🔎 이 질문, 어느 블록이 나와야 하나요?" on:close={() => (feedback = null)}>
			{#if feedback}
				<div class="tw-space-y-3 tw-font-sans">
					<p class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
						<b class="tw-text-foreground">"{feedback.utterance}"</b> 질문이
						{#if feedback.routing?.matched}
							지금은 <b class="tw-text-foreground">"{feedback.routing.blockDescription || "(설명 없음)"}"</b> 블록으로 갔어요.
						{:else}
							지금은 자유 답변으로 처리됐어요.
						{/if}
						<br />나와야 할 블록을 고르면 그 블록의 <b class="tw-text-foreground">'이럴 때 보여주세요'</b>에
						이 질문이 예시로 추가돼요 — 다음부턴 이런 질문이 그 블록으로 가요.
					</p>
					<div class="tw-space-y-1">
						{#each blocks.filter((b) => b.id) as b (b.id)}
							<button
								type="button"
								class="tw-block tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-p-2 tw-text-left tw-text-xs tw-text-foreground tw-transition-colors hover:tw-border-primary hover:tw-bg-muted disabled:tw-cursor-default disabled:tw-opacity-50"
								disabled={b.id === feedback.routing?.blockId}
								on:click={() => applyFeedbackTo(b)}
							>
								{b.description || "(이름 없음)"}
								{#if b.id === feedback.routing?.blockId}
									<span class="tw-ml-1 tw-text-muted-foreground">← 지금 나온 블록</span>
								{/if}
							</button>
						{/each}
					</div>
					<p class="tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
						자유 답변이 맞는 질문이었다면 그냥 닫으세요.
					</p>
				</div>
			{/if}
		</Sheet>
		{/if}

		{#each displayBlocks as block, i (block.id ?? "new-" + i)}
			{#if isTestChat}
				<!-- 테스트 챗은 카드가 둘뿐인데 똑같이 생기면 어느 게 무엇인지 알 수 없다.
				     역할을 머리에 박아 "①진행 → ②마무리" 한 줄기로 읽히게 한다.
				     ⚠️ 역할이 없는 블록도 숨기지 않는다 — 숨기면 지울 수도 없어 중복이 쌓인다.
				     ⭐ 다만 **되돌아갈 문을 반드시 함께 준다.** 테스트 챗에서는 역할 선택지를 감췄기 때문에
				        ([🎉 설정 완료 화면] 버튼이 없다), 이 버튼이 없으면 한 번 역할을 잃은 블록은
				        지우는 것 말고 출구가 없다. 그러면 저작자는 [+ 완료 화면 만들기]로 새로 만들게 되고
				        — 그 버튼은 doneBlock 이 없을 때 다시 나타나므로 — 눌러둔 만큼 중복이 쌓인다.
				        (실제로 그렇게 4개가 쌓였다. 문구가 아니라 이 한쪽으로만 열리는 문이 원인이었다) -->
				{@const isProbe = block.completion?.type === "probe"}
				{@const isDone = block.role === "intakeDone"}
				<div class="test-step-head" class:unused={!isProbe && !isDone}>
					<span class="test-step-no">{isProbe ? "①" : isDone ? "②" : "·"}</span>
					<div class="test-step-text">
						<b>
							{#if isProbe}테스트 진행
							{:else if isDone}끝나면 보여줄 화면
							{:else}아직 어디에도 쓰이지 않아요{/if}
						</b>
						<span class="test-step-sub">
							{#if isProbe}여는 말·진행 지침·문항을 여기서 정합니다
							{:else if isDone}판정이 끝나면 이 화면이 뜨고 목록으로 돌아갑니다. AI 텍스트 칸에 쓴 지침대로 말해요 — 테스트 소감을 줄지 말지도 거기서 정합니다
							{:else}완료 화면으로 쓸 거면 지정하고, 아니면 지워주세요{/if}
						</span>
					</div>
					{#if !isProbe && !isDone}
						<button
							type="button"
							class="test-step-promote"
							title={!block.id
								? "먼저 블록을 저장하세요"
								: doneBlock
									? "이 블록을 완료 화면으로 씁니다(지금 ②인 블록은 역할이 풀립니다)"
									: "판정이 끝났을 때 이 블록을 보여줍니다"}
							disabled={!block.id}
							on:click={() => setIntakeDoneBlock(block)}
							>② 완료 화면으로 지정</button
						>
					{/if}
				</div>
			{/if}
			{#if viewMode === "flows"}
				<div class="flow-step-head">
					<span class="flow-step-no">{i + 1}</span>
					<!-- 이 번호는 "이 레슨에서의 순서"다. 카드 안 #N(블록 번호)과 다른 축이라 말로 구분해 둔다. -->
					<span class="flow-step-title">이 레슨의 {i + 1}번째</span>
					<span class="head-spacer" />
					<button
						type="button"
						class="mini"
						on:click={() => moveStepById(block.id, -1)}
						disabled={i === 0}>↑</button
					>
					<button
						type="button"
						class="mini"
						on:click={() => moveStepById(block.id, 1)}
						disabled={i === displayBlocks.length - 1}>↓</button
					>
					<button
						type="button"
						class="mini del"
						on:click={() => removeStepById(block.id)}>플로우에서 빼기</button
					>
				</div>
			{/if}
			<div
				class="block-card"
				class:dirty={block._dirty}
				class:in-flow={viewMode === "flows"}
				class:in-test={isTestChat}
				tabindex="-1"
				on:focusin={() => postPreview(block)}
				on:focusout={(e) => autoSaveOnBlur(block, e)}
			>
				<div class="block-head">
					<span class="block-num">#{blocks.indexOf(block) + 1}</span>
					<!-- 블록이 기본 단위이고, 완료 조건이 붙으면 그 블록이 "스텝"이 된다.
					     카드 겉모습이 같아 헷갈리므로 정체를 배지로 명시한다. -->
					<span class="block-kind" class:step={isStep(block)}
						>{isStep(block) ? "스텝" : "자유 블록"}</span
					>
					<span class="block-name"
						>{block.description || "(이름 없음)"}</span
					>
					<span class="head-spacer" />
					<!-- ⚠️ 테스트 챗에서는 역할을 고르게 하지 않는다. 카드 둘의 자리가 곧 역할이고
					     (①판정 블록 = 시작 / ②완료 화면), 서버가 저장할 때 그렇게 붙인다.
					     선택지를 남겨두면 "첫 대화 블록"처럼 이 챗에서 의미 없는 개념이 노출된다. -->
					{#if !isTestChat}
						<button
							type="button"
							class="entry-btn"
							class:active={block.isEntry}
							title={block.id
								? "이 블록으로 첫 대화를 시작합니다"
								: "먼저 블록을 저장하세요"}
							disabled={!block.id}
							on:click={() => setEntryBlock(block)}
							>🚩 첫 대화 블록</button
						>
						<button
							type="button"
							class="entry-btn"
							class:active={block.role === "intakeDone"}
							title={block.id
								? "사전 설정이 끝난 턴에 이 블록을 함께 보여주고 목록으로 이동합니다"
								: "먼저 블록을 저장하세요"}
							disabled={!block.id}
							on:click={() => setIntakeDoneBlock(block)}
							>🎉 설정 완료 화면</button
						>
					{/if}
				</div>
				<div class="block-row">
					<!-- 같은 description 필드지만 역할이 다르다:
					     자유 블록 = 자유 발화 라우팅의 트리거(셀렉터가 이 문구로 고름)
					     스텝      = 이 스텝의 "주제"(라우팅 대상 아님 — 서버 getRoutingCatalog가 스텝 제외).
					                  유도 발화/완료 판정 프롬프트에 [진행 중 스텝]·[이 스텝의 주제]로 들어간다. -->
					<div class="tab-text sub2">
						{viewMode === "flows" ? "스텝 이름 · 주제" : "이럴 때 보여주세요"}
					</div>
					<input
						class="text-field"
						type="text"
						placeholder={viewMode === "flows"
							? "이 스텝에서 무엇을 하는지. 예: 교실 찾기 — 교실이 어디인지 영어로 묻게 하기"
							: '주제 + 고객이 실제로 할 질문 예시. 예: 예약 문의 (예: "예약하고 싶어요", "몇 시에 갈 수 있어요?")'}
						bind:value={block.description}
						on:input={() => markDirty(block)}
					/>
					<div class="tab-text sub field-hint">
						{#if viewMode === "flows"}
							스텝 목록에 보이는 <b>이름</b>이자, AI가 참고하는 <b>이 스텝의 주제</b>예요.
							스텝은 자유 발화로 골라지지 않으니(플로우 순서·버튼으로만 진입)
							고객 질문 예시는 넣지 마세요 — 무엇을 하는 단계인지 한 줄로 적어요
						{:else}
							AI가 이 설명을 보고 언제 이 블록을 띄울지 판단해요 —
							고객이 실제로 할 법한 <b>질문 문장</b>을 2~3개 넣어주면 훨씬 정확해져요
						{/if}
					</div>
				</div>

				<!-- 이 스텝의 상황 — 주제(한 줄) 바로 아래. 서버 조립 순서(주제 → 상황 → 목표)와 같고,
				     여는 말을 쓰기 전에 무대를 먼저 정하게 해 "여는 말에 지침까지 쏟는" 실수를 막는다.
				     '무대' 축(세계관 → 상황)이라 '어떻게 이끌지' 축(유도 방식·유도문형)과 떼어 놓았다. -->
				{#if viewMode === "flows" && isStep(block)}
				<div class="block-sub">
					<div class="block-row">
						<!-- 장면 유형 — 아래 상황 글이 어떤 장면인지. 고르면 칸 이름·예시가 바뀌고, 유형 규칙은 엔진이 싣는다.
						     목표형·자유 대화 스텝에만(판정·평가는 장면이 아니라 측정이다). -->
						{#if block.completion.type === "objectives" || block.completion.type === "converse"}
							<div class="scene-row">
								<span class="tab-text sub2">장면 유형</span>
								<Select
									class="tw-w-auto tw-min-w-[240px]"
									value={block.completion.scene?.type || ""}
									on:change={(e) => setSceneType(block, e.target.value)}
								>
									<option value="">지정 안 함 — 아래 글만 따름</option>
									{#each SCENE_TYPES as t (t.key)}
										<option value={t.key}>{t.label} — {t.desc}</option>
									{/each}
								</Select>
								{#if sceneOf(block)?.needsRole}
									<input
										class="text-field scene-role"
										type="text"
										maxlength="40"
										placeholder={sceneOf(block).rolePlaceholder}
										value={block.completion.scene?.role || ""}
										on:input={(e) => setSceneRole(block, e.currentTarget.value)}
									/>
								{/if}
							</div>
						{/if}
						<div class="tab-text sub2">{sceneOf(block)?.situationLabel || "이 스텝의 상황"}</div>
						<textarea
							class="text-field situation-field"
							rows="3"
							placeholder={sceneOf(block)?.placeholder ||
								"이 스텝에서 벌어지는 일 — 예: 반 친구로서 학교 첫날 이야기를 이어간다. 학습자가 스스로 영어로 말하게 두고, 답을 대신 완성해 주지 않는다."}
							bind:value={block.completion.situation}
							use:autoGrow={block.completion.situation}
							on:input={() => markDirty(block)}
						/>
						<!-- 세계관(챗 전역)과 같은 '무대' 축임을 밝혀, 옆의 [유도 방식]·[유도문형]('어떻게 이끌지' 축)과
						     헷갈리지 않게 한다. "대화 내내"가 예전 [매 턴] 칩이 하던 말을 대신한다. -->
						<div class="tab-text sub field-hint">
							{#if sceneOf(block)}
								무대만 적어요 — '{sceneOf(block).label}' 규칙(누가 무엇을 정하는지·역할)과 여는 말의 장면 소개 방식은 자동으로 실려요
							{:else}
								챗 세계관 위에, 이 스텝에서만 달라지는 무대·역할·흐름을 적어요 — 대화 내내 참고합니다
							{/if}
						</div>
						<!-- 쓸 수 있는 변수 — 누르면 위 칸 끝에 붙는다. 실제 대화에선 이 학습자의 값으로 바뀐다 -->
						<div class="var-chips">
							<span class="tab-text sub">학습자 값 넣기</span>
							{#each inputKeys as k}
								<button type="button" class="var-chip" title={`{{${k}}} — 이 학습자의 ${k} 값`} on:click={() => insertVar(block, k)}>{`{{${k}}}`}</button>
							{/each}
							{#each variants(block) as v}
								<button type="button" class="var-chip var-chip-own" title={`{{${v.name}}} — 아래 학습자별 내용(${v.by} 기준)`} on:click={() => insertVar(block, v.name)}>{`{{${v.name}}}`}</button>
							{/each}
						</div>
					</div>

					<!-- 레벨별 낱말 — 사전 테스트를 연결해야 열린다(레벨 목록이 테스트에서 오므로).
					     위 레벨일수록 더 다양하고 어려운 낱말. 대화에는 이 학습자 레벨의 줄만 들어간다(서버 promptVars). -->
					<div class="block-row">
						<div class="tab-text sub2">레벨별 낱말</div>
						{#if levelDecl}
							{#each knownValues(levelDecl) as lv, li (lv)}
								<div class="collect-row trait-guide-row">
									<span class="variant-val">{lv}{levelDecl.default === lv ? " (기본)" : ""}</span>
									<input
										class="text-field"
										type="text"
										value={levelWords(block)?.map?.[lv] ?? ""}
										placeholder={levelWordsHint(li, knownValues(levelDecl).length)}
										on:input={(e) => setLevelWords(block, lv, e.currentTarget.value)}
									/>
								</div>
							{/each}
							<div class="collect-row">
								<span class="tab-text sub field-hint">
									🔗 {levelDecl.from.title} 레벨마다 — 위로 갈수록 더 다양하고 어려운 낱말·짧은 구를 적어요. 문장은 쓰지 않아요(막혔을 때
									하나씩 건네는 재료라, 문장이면 정답을 통째로 주게 돼요).
								</span>
								{#if levelWords(block)}
									<span class="head-spacer" />
									<button type="button" class="mini del" on:click={() => clearLevelWords(block)}>비우기</button>
								{/if}
							</div>
						{:else}
							<div class="tab-text sub field-hint">
								<button type="button" class="link-btn" on:click={goSettings}>[대화 설정] → 학습자 정보 → 레벨 받아오기</button>에서 사전 테스트를 고르면 레벨마다 낱말을 적는 칸이 생겨요.
							</div>
						{/if}
					</div>

					<!-- 학습자별 내용 — 같은 스텝이라도 학습자 정보(예: 레벨)에 따라 달라지는 글.
					     ⭐ 고르는 일은 서버가 한다: AI 에게는 이 학습자에게 맞는 한 벌만 간다(util/promptVars.js).
					        예전처럼 세 벌을 지침에 다 써 두고 "맞는 걸 골라라"고 하면 틀린 줄을 쓰고 토큰도 세 배다. -->
					<div class="block-row">
						<div class="tab-text sub2">학습자별 내용</div>
						{#each variants(block) as v, vi}
							{#if !(levelDecl && v.name === LEVEL_WORDS_VAR)}
							<div class="variant-box">
								<div class="collect-row">
									<input class="text-field variant-name" type="text" value={v.name} title={"지침에서 {{이름}} 으로 씁니다"}
										on:change={(e) => setVariant(block, vi, { name: e.currentTarget.value.trim().replace(/[^a-zA-Z0-9_]/g, "").replace(/^[^a-zA-Z]+/, "") || v.name })} />
									<span class="tab-text sub">←</span>
									<select class="text-field variant-by" value={v.by} on:change={(e) => setVariant(block, vi, { by: e.currentTarget.value })}>
										{#each variantByKeys as k}<option value={k}>{k} 에 따라</option>{/each}
										{#if !variantByKeys.includes(v.by)}<option value={v.by}>{v.by} (선언 안 됨)</option>{/if}
									</select>
									<span class="head-spacer" />
									<button type="button" class="mini del" on:click={() => removeVariant(block, vi)}>삭제</button>
								</div>
								{#each Object.entries(v.map || {}) as [val, text] (val)}
									<div class="collect-row trait-guide-row">
										<span class="variant-val">{val}{traitByKey(v.by)?.default === val ? " (기본)" : ""}</span>
										<textarea class="text-field" rows="1" placeholder={`${v.by} 가 ${val} 인 학습자에게 들어갈 내용`}
											value={text} use:autoGrow={text}
											on:input={(e) => setVariantText(block, vi, val, e.currentTarget.value)} />
									</div>
								{/each}
								{#if !Object.keys(v.map || {}).length}
									<div class="tab-text sub field-hint">
										‘{v.by}’ 항목에 정해진 값이 없어 칸을 만들 수 없어요 — <button type="button" class="link-btn" on:click={goSettings}>[대화 설정] → 학습자 정보</button>에서 ‘값마다 지시문 따로’로 값(예: 🍃 🌸 🍎)을 먼저 정해 주세요
									</div>
								{/if}
							</div>
							{/if}
						{/each}
						<div class="collect-row">
							<button type="button" class="mini add" on:click={() => addVariant(block)} disabled={!variantByKeys.length}>+ 학습자별 내용 추가</button>
							{#if !variantByKeys.length}
								<span class="tab-text sub">먼저 <button type="button" class="link-btn" on:click={goSettings}>[대화 설정] → 학습자 정보</button>에 항목을 만들어 주세요</span>
							{/if}
						</div>
					</div>
					<!-- 예전 '추가 상황 설명'(situationNote)이 남아 있는 스텝 — 그 칸을 없앴더니 값이 DB에만
					     남아 화면에서 사라졌다. 안 보이는 값이 대화를 끌고 가면 안 되므로 꺼내 보여주고
					     합치거나 지우게 한다. (서버는 [이 스텝의 상황]이 채워져 있으면 이 값을 무시한다) -->
					{#if (block.completion.situationNote || "").trim()}
						<div class="legacy-note">
							<div class="legacy-note-head">
								예전 <b>‘추가 상황 설명’</b>이 남아 있어요.
								{#if (block.completion.situation || "").trim()}
									지금은 <b>쓰이지 않습니다</b>([이 스텝의 상황]이 우선).
								{:else}
									[이 스텝의 상황]이 비어 있어 <b>이 값이 대신 쓰이는 중</b>이에요.
								{/if}
							</div>
							<textarea class="text-field legacy-note-body" rows="3" readonly value={block.completion.situationNote} use:autoGrow={block.completion.situationNote} />
							<div class="legacy-note-row">
								<button type="button" class="mini" on:click={() => mergeLegacyNote(block)}>[이 스텝의 상황]에 합치기</button>
								<button type="button" class="mini del" on:click={() => dropLegacyNote(block)}>지우기</button>
							</div>
						</div>
					{/if}
				</div>
				{/if}

				<!-- 콘텐츠 — 블록 카드와 코스 화면(평가)이 같은 편집기를 쓴다(BlockContentsEditor).
				     ⚠️ 제목(block-sub + "콘텐츠")은 편집기가 스스로 그린다 — 여기서 또 감싸면 제목이 두 번 나온다. -->
				<BlockContentsEditor
					{block}
					{schemas}
					{assetMap}
					chatId={chat.chatId}
					firstSlotNote={isStep(block)
						? "이 스텝을 열 때 처음 한 번 나가는 말이에요. 대화 내내 따를 내용은 위 [이 스텝의 상황] 칸에 쓰세요."
						: ""}
					on:change={() => markDirty(block)}
					on:pickImage={(e) => openImagePicker(block, e.detail.content, e.detail.key)}
					on:pickGallery={(e) => openGalleryPicker(block, e.detail.content)}
				/>

				<!-- 버튼 -->
				<div class="block-sub">
					<div class="tab-text sub2">
						버튼 <button
							class="mini add"
							on:click={() => addButton(block)}>+ 추가</button
						>
					</div>
					{#each block.buttons as btn, bi}
						<div class="btn-item">
							<div class="block-row inline btn-row">
								<Select
									class="tw-flex-none tw-w-[120px]"
									bind:value={btn.type}
									on:change={() => markDirty(block)}
								>
									<option value="followup">연관질문</option>
									<option value="navigate">블록 이동</option>
								</Select>
								<input
									class="text-field"
									type="text"
									placeholder="버튼 라벨"
									bind:value={btn.label}
									on:input={() => markDirty(block)}
								/>
								{#if btn.type === "navigate"}
									<Select
										class="tw-flex-none tw-w-[120px]"
										invalid={!!btn.label && !btn.targetBlockId}
										bind:value={btn.targetBlockId}
										on:change={() => markDirty(block)}
									>
										<option value="">대상 블록</option>
										{#each targetOptions as t}<option
												value={t.id}
												>#{blocks.indexOf(t) + 1} · {truncate(
													t.description || "(설명 없음)",
												)}</option
											>{/each}
									</Select>
								{:else}
									<input
										class="text-field"
										type="text"
										placeholder="질문 텍스트 (비우면 라벨 사용)"
										bind:value={btn.text}
										on:input={() => markDirty(block)}
									/>
								{/if}
								<button
									type="button"
									class="mini del"
									on:click={() => removeButton(block, bi)}
									>삭제</button
								>
							</div>
							<!-- 저장에서 빠질 행은 그 자리에서 알린다 — 판정은 cleanButtons 와 같은 함수. -->
							{#if buttonDropReason(btn)}
								<div class="btn-warn">
									⚠︎ {buttonDropReason(btn)}. (이대로 두면 이 버튼은 저장되지 않아요)
								</div>
							{/if}
						</div>
					{/each}
					{#if block._buttonsDropped > 0}
						<!-- 이미 저장이 나간 뒤의 사후 통보. 행 경고를 못 보고 넘어간 경우를 잡는다
						     (여기까지 없으면 리프레시해야 비로소 사라진 걸 안다). -->
						<div class="btn-warn">
							⚠︎ 방금 저장에서 버튼 {block._buttonsDropped}개가 빠졌어요. 위 ⚠︎ 표시를 채운 뒤 다시 저장해 주세요.
						</div>
					{/if}
				</div>

				{#if viewMode === "flows" || isTestChat}
				<!-- 유도 목표(스텝) — ⑨: 진입하면 목표를 유도하는 대화가 이어지고, 다 채우면 다음 스텝으로 전진 -->
				<div class="block-sub">
					<div class="tab-text sub2">
						{isTestChat ? "판정 기준" : "스텝 · 완료 조건"}
						{#if !isTestChat}
							{#if isStep(block)}
								<button class="mini del" on:click={() => disableStep(block)}
									>스텝 해제</button
								>
							{:else}
								<button class="mini add" on:click={() => enableStep(block)}
									>+ 스텝으로 만들기</button
								>
							{/if}
						{/if}
					</div>
					{#if isStep(block)}
						<!-- ⚠️ 이 {#if} 는 완료조건 **편집기 전체**를 감싼다(문항·구간·지침까지).
						     여기에 조건을 더 붙이면 테스트 챗에서 편집기가 통째로 사라진다 —
						     실제로 그렇게 만들어 문항이 안 보였다. 숨길 건 각 요소를 따로 감쌀 것. -->
						{#if !isTestChat}
							<div class="step-hint">
								<b>블록에 완료 조건을 붙이면 스텝</b>이 돼요. 진입하면 아래 조건을 향한 대화가
								이어지고, 조건을 채우면 자동으로 다음 스텝으로 넘어가요(같은 블록이 다시 나오지
								않아요). 조건이 없는 블록은 자유 대화에서 질문에 맞춰 불려 나오는
								<b>자유 블록</b>이에요.
							</div>
						{/if}
						{#if !isTestChat}
						<label class="step-opt step-type">
							완료 유형
							<Select
								class="tw-w-[260px] tw-max-w-full"
								bind:value={block.completion.type}
								on:change={() => changeCompletionType(block)}
							>
								<option value="objectives">유도 목표 — 목표를 대화로 유도</option>
								<option value="converse">자유 대화 — 정해진 횟수만큼 주고받으면 완료</option>
								<option value="judge">평가 — 대화를 채점해 통과</option>
								<option value="probe">판정(레벨 확인) — 문항으로 수준을 가려 프로필에 남김</option>
							</Select>
						</label>
						{/if}
						{#if block.completion.type === "probe" && block.completion.sameAs}
							<!-- 사후 점검: 사전 테스트와 같은 문항. 원본을 보여만 주고 고치는 곳은 사전 테스트다. -->
							<div class="step-order-hint">
								<b>사전 테스트와 같은 문항을 씁니다</b> —
								'{block.completion.sameAs.title || linkedProbe?.description || "사전 테스트"}'의 진행 지침·문항·구간·중단 규칙·결과 항목을 그대로 가져와요.
								사전 테스트를 고치면 여기도 함께 바뀌어 <b>사전·사후 비교가 깨지지 않습니다.</b>
								이 채팅에서 따로 고칠 것은 이 블록의 <b>첫 인사</b>와 <b>완료 화면</b> 블록뿐이에요.
							</div>
							{#if linkedProbe}
								<div class="probe-hint">
									<b>문항 {(linkedProbe.completion.questions || []).length}개</b>
									· 연속 {linkedProbe.completion.stopAfter ?? 3}개 미달이면 중단
									· 워밍업 {linkedProbe.completion.warmup ?? 0}
									{#if linkedProbe.completion.resultKey}· 결과 항목 {linkedProbe.completion.resultKey}{linkedProbe.completion.scoreKey ? ` / ${linkedProbe.completion.scoreKey}` : ""}{/if}
								</div>
								{#each linkedProbe.completion.questions || [] as q}
									<div class="collect-row probe-q">
										<span class="probe-num">레벨 {q.level}</span>
										<span class="linked-q">{q.text}</span>
									</div>
								{/each}
							{:else}
								<div class="probe-hint">원본 문항을 불러오는 중입니다… 계속 비어 있으면 사전 테스트에 판정 문항이 있는지 확인해 주세요.</div>
							{/if}
							<div class="collect-row">
								<button type="button" class="mini add"
									on:click={() => window.open(`/chat-builder/${block.completion.sameAs.chatId}`, "_blank")}>사전 테스트에서 문항 고치기 ↗</button>
								<button type="button" class="mini del" disabled={!linkedProbe} on:click={() => unlinkProbe(block)}>연결 끊고 따로 쓰기</button>
							</div>
						{:else if block.completion.type === "probe"}
							<!-- ⚠️ 이 칸이 없으면 영어 레벨 테스트인데 한국어로 물어보는 일이 생긴다.
							     문항은 영어로 저작해도, 캐릭터가 그 문항을 '건네는 말'은 지침이 없으면
							     챗의 기본 언어를 따라간다. 매 턴 프롬프트에 실린다. -->
							<label class="step-opt step-rubric">
								진행 지침 <span class="opt-sub">매 턴 AI가 따르는 흐름 — 언어·말투·태도를 여기에</span>
								<textarea
									class="text-field situation-field"
									placeholder={"예) 질문과 반응은 모두 영어로 한다. 한국어로 답해도 영어로 이어간다.\n시험이 아니라 가벼운 대화처럼. 채점·정답 여부는 절대 말하지 않는다."}
									bind:value={block.completion.situation}
									on:change={() => markDirty(block)}
								/>
							</label>
							<div class="step-order-hint">
								<b>문항 하나가 레벨 하나</b>예요. 쉬운 것부터 순서대로 묻고,
								<b>통과한 가장 높은 문항의 레벨</b>이 결과가 됩니다.
							</div>
							<div class="collect-row">
								<span class="probe-num">연속 미달 몇 개면 중단
									<input class="text-field tw-w-[56px]" type="number" min="1"
										bind:value={block.completion.stopAfter} on:change={() => markDirty(block)} /></span>
								<span class="probe-num" title="앞 N문항은 채점하지 않습니다. 첫 질문이 어려우면 위축돼 이후 답이 전부 짧아져요.">워밍업(채점 제외)
									<input class="text-field tw-w-[56px]" type="number" min="0"
										bind:value={block.completion.warmup} on:change={() => markDirty(block)} /></span>
							</div>

							<div class="probe-hint"><b>결과를 담을 프로필 항목</b> — 위 <b>사전 설정</b>에 같은 키를 <b>이 채팅에서 받기</b>로 선언해야 저장됩니다.</div>
							<div class="collect-row">
								<input class="text-field tw-w-[190px]" type="text" placeholder="구간 이름을 담을 키 — 예: englishTier"
									bind:value={block.completion.resultKey} on:change={() => markDirty(block)} />
								<input class="text-field tw-w-[190px]" type="text" placeholder="숫자를 담을 키(선택) — 예: englishLevel"
									bind:value={block.completion.scoreKey} on:change={() => markDirty(block)} />
							</div>
							<div class="probe-hint">
								대화 지시문은 <b>구간 이름</b>에 거는 걸 권해요 — 숫자에 걸면 레벨 수만큼 지시문을 써야 합니다.
							</div>

							<div class="probe-hint"><b>구간</b> — 숫자를 사람이 읽는 이름으로 (이모지도 됩니다)</div>
							{#each block.completion.tiers || [] as tier, ti}
								<div class="collect-row probe-q">
									<input class="text-field tw-w-[64px]" type="number" min="0" bind:value={tier.from} on:change={() => markDirty(block)} />
									<span class="probe-num">~</span>
									<input class="text-field tw-w-[64px]" type="number" min="0" bind:value={tier.to} on:change={() => markDirty(block)} />
									<input class="text-field tw-w-[160px]" type="text" placeholder="이름 — 예: 나뭇잎"
										bind:value={tier.label} on:change={() => markDirty(block)} />
									<button type="button" class="mini del" on:click={() => removeTier(block, ti)}>삭제</button>
								</div>
							{/each}
							<div class="collect-row probe-q">
								<button type="button" class="mini add" on:click={() => addTier(block)}>+ 구간 추가</button>
							</div>

							<div class="probe-hint"><b>문항</b> — 쉬운 것부터. 레벨 숫자가 곧 결과값입니다</div>
							{#each block.completion.questions || [] as q, qi}
								<div class="probe-band">
									<div class="collect-row">
										<span class="probe-num">레벨
											<input class="text-field tw-w-[56px]" type="number" min="1"
												bind:value={q.level} on:change={() => markDirty(block)} /></span>
										<input class="text-field" type="text" placeholder="문항 — 예: What did you do yesterday?"
											bind:value={q.text} on:change={() => markDirty(block)} />
										<button type="button" class="mini del" on:click={() => removeProbeQ(block, qi)}>삭제</button>
									</div>
									<div class="collect-row probe-q">
										<input class="text-field" type="text"
											placeholder="전형적인 통과 예시(선택) — 최소 기준이 아니라 '이 정도면 통과'"
											bind:value={q.sample} on:change={() => markDirty(block)} />
									</div>
									<div class="collect-row probe-q">
										<input class="text-field" type="text"
											placeholder="이 문항의 통과 기준(선택) — 예: 과거형으로 한 문장 이상"
											bind:value={q.criteria} on:change={() => markDirty(block)} />
									</div>
								</div>
							{/each}
							<div class="collect-row">
								<button type="button" class="mini add" on:click={() => addProbeQ(block)}>+ 문항 추가</button>
							</div>
						{/if}
						{#if block.completion.type === "objectives"}
						<div class="step-order-hint">
							<b>①</b> 어떻게 이끌지 먼저 정하고 → <b>②</b> 그 방식에 맞게 목표를 쓰세요.
							목표 칸의 예시와 아래 점검이 고른 방식에 맞춰 바뀌어요.
						</div>
						<label class="step-opt guide-style">
							유도 방식
							<Select
								value={block.completion.guideStyle?.preset || ""}
								on:change={(e) => {
									setGuideStylePreset(block.completion, e.target.value);
									markDirty(block);
								}}
							>
								{#each GUIDE_STYLE_OPTIONS as opt}
									<option value={opt.key}>{opt.label}</option>
								{/each}
							</Select>
						</label>
						{#if block.completion.guideStyle?.preset === "custom"}
							<textarea
								class="text-field"
								rows="3"
								placeholder="이 스텝에서 AI가 어떻게 이끌지 직접 작성 — 예: 정답을 통째로 알려주지 말고 시작 부분만 힌트로 유도한다"
								bind:value={block.completion.guideStyle.custom}
								use:autoGrow={block.completion.guideStyle.custom}
								on:input={() => markDirty(block)}
							/>
						{/if}
						<label class="step-opt guide-style">
							유도문형
							<Select
								value={block.completion.initiative || ""}
								on:change={(e) => {
									setInitiative(block.completion, e.target.value);
									markDirty(block);
								}}
							>
								{#each INITIATIVE_OPTIONS as opt}
									<option value={opt.key}>{opt.label}</option>
								{/each}
							</Select>
						</label>
						<div class="step-objs-head">
							장면 정보 <span class="opt-sub">캐릭터만 아는 것(값·시간·생각 등) — 목표에서 [알아낼 것]으로 고르면 학습자가 물을 때까지 먼저 말하지 않아요</span>
						</div>
						{#each block.completion.facts || [] as f, fi}
							<div class="block-row inline">
								<input
									class="text-field fact-key"
									type="text"
									placeholder="이름 (예: 표 값)"
									bind:value={f.key}
									on:input={() => markDirty(block)}
								/>
								<input
									class="text-field"
									type="text"
									placeholder="내용 (예: 한 장에 8,000원)"
									bind:value={f.value}
									on:input={() => markDirty(block)}
								/>
								<button type="button" class="mini del" on:click={() => removeFact(block, fi)}>삭제</button>
							</div>
						{/each}
						<button class="mini add" on:click={() => addFact(block)}>+ 장면 정보 추가</button>
						<div class="step-objs-head">
							유도할 목표 <span class="opt-sub">사용자가 해내면 다음 스텝으로 넘어가요</span>
						</div>
						<!-- 짧은 이름을 하나라도 적으면 채팅 화면 진행바 아래에 목표 체크리스트가 뜬다(서버 blockUtil.objectiveChecklist).
						     안 적으면 체크리스트가 없다 — 병원 예약 같은 흐름에 '할 일' 목록이 갑자기 뜨지 않게 저작자가 켜는 기능이다. -->
						<div class="step-order-hint">
							목표마다 <b>짧은 이름</b>을 적으면 채팅 화면에 <b>체크리스트</b>로 보이고, 사용자가 해낸 목표에 체크가 켜져요.
							제목은 장면 유형에 따라 '오늘 할 일'·'이야기 거리'·'미션'으로 바뀌어요.
						</div>
						{#each block.completion.objectives as obj, oi}
							<div class="block-row inline">
								<input
									class="text-field"
									type="text"
									placeholder={objectivePlaceholder(block.completion)}
									bind:value={obj.criterion}
									on:input={() => markDirty(block)}
								/>
								<button
									type="button"
									class="mini"
									on:click={() => moveObjective(block, oi, -1)}
									disabled={oi === 0}>↑</button
								>
								<button
									type="button"
									class="mini"
									on:click={() => moveObjective(block, oi, 1)}
									disabled={oi === block.completion.objectives.length - 1}
									>↓</button
								>
								<button
									type="button"
									class="mini del"
									on:click={() => removeObjective(block, oi)}>삭제</button
								>
							</div>
							<div class="find-row">
								<span class="opt-sub">짧은 이름</span>
								<input
									class="text-field obj-label"
									type="text"
									maxlength="20"
									placeholder="체크리스트에 보일 이름 (예: 날짜 정하기)"
									bind:value={obj.label}
									on:input={() => markDirty(block)}
								/>
							</div>
							{#if factKeys(block.completion).length}
								<div class="find-row">
									<span class="opt-sub">알아낼 것</span>
									{#each factKeys(block.completion) as k (k)}
										<button
											type="button"
											class="mini find"
											class:on={Array.isArray(obj.find) && obj.find.includes(k)}
											on:click={() => toggleFind(block, obj, k)}>{k}</button
										>
									{/each}
								</div>
							{/if}
						{/each}
						<button class="mini add" on:click={() => addObjective(block)}
							>+ 목표 추가</button
						>
						{:else if block.completion.type === "converse"}
						<div class="step-order-hint">
							채울 목표 없이 <b>정해진 횟수만큼 주고받으면</b> 완료돼요. 무슨 얘기를 어떻게 주고받을지는 위 <b>[이 스텝의 상황]</b>에 적어주세요.
						</div>
						<label class="step-opt">
							대화 횟수 (사용자가 몇 번 말하면 완료)
							<input
								class="text-field step-num"
								type="number"
								min="1"
								max="20"
								bind:value={block.completion.minTurns}
								on:input={() => markDirty(block)}
							/>
						</label>
						{:else if block.completion.type === "judge"}
							<label class="step-opt step-rubric">
								채점 루브릭
								<textarea
									class="text-field"
									rows="4"
									placeholder="무엇을 얼마나 잘하면 통과인지 — 예: 학습자가 영어로 인사·자기소개·주문을 자연스럽게 하면 높게 채점"
									bind:value={block.completion.rubric}
									use:autoGrow={block.completion.rubric}
									on:input={() => markDirty(block)}
								/>
							</label>
							<label class="step-opt">
								통과 점수
								<input
									class="text-field step-num"
									type="number"
									min="1"
									max="100"
									bind:value={block.completion.passScore}
									on:input={() => markDirty(block)}
								/>
							</label>
						{/if}
						{#if block.completion.type === "objectives" || block.completion.type === "converse"}
							<!-- 이 스텝만 쓰는 말·받아 주기를 다르게 — 빈 칸은 챗 기본값을 따른다(축 단위 상속) -->
							<details class="step-reply" open={!!replySummary(block.completion.reply)}>
								<summary>
									쓰는 말 · 학습자 말 받아 주기
									<span class="opt-sub">{replySummary(block.completion.reply) ? `이 스텝만: ${replySummary(block.completion.reply)}` : "챗 기본값 따름"}</span>
								</summary>
								<ReplyStylePicker
									value={block.completion.reply}
									inherited={chat.replyStyle || null}
									on:change={(e) => setStepReply(block, e.detail)}
								/>
							</details>
						{/if}
						<div class="step-opts">
							{#if block.completion.type === "objectives"}
							<label class="step-opt">
								완료 기준
								<Select
									bind:value={block.completion.minCovered}
									on:change={() => markDirty(block)}
								>
									<option value={null}>모든 목표 충족</option>
									{#each block.completion.objectives as _o, k}
										<option value={k + 1}>{k + 1}개 충족</option>
									{/each}
								</Select>
							</label>
							{/if}
							<!-- 최소 대화 턴 = 길이 목표(서버 ObjectivesCompletion.minTurns). 목표를 다 채워도 이 턴까지는
							     학습자가 한 말을 넓히며 이어 간다 — 목표 수만으로는 몰아서 말하면 한두 턴에 끝났다. 비우면 예전처럼 목표만 채우면 끝. -->
							{#if block.completion.type === "objectives"}
							<label class="step-opt" title="목표를 다 채워도 이 턴까지는 학습자가 한 말을 넓히며 이어 가요. 비우면 목표만 채우면 끝나요.">
								최소 대화 턴
								<input
									class="text-field step-num"
									type="number"
									min="0"
									placeholder="없음"
									bind:value={block.completion.minTurns}
									on:input={() => markDirty(block)}
								/>
							</label>
							{/if}
							<!-- 최대 턴 = [건너뛰기] 노출 시점(스텝에 갇혔을 때의 탈출구).
							     자유 대화는 판정 없이 '대화 횟수'에서 반드시 끝나므로 갇힐 수가 없다 → 턴 설정은 하나만 둔다. -->
							{#if block.completion.type !== "converse"}
							<label class="step-opt">
								최대 턴
								<input
									class="text-field step-num"
									type="number"
									min="1"
									bind:value={block.completion.maxTurns}
									on:input={() => markDirty(block)}
								/>
							</label>
							{/if}
						</div>
						<!-- 예전에 지정한 [다음 스텝](completion.next)이 남아 있는 스텝 — 칸을 없앤 뒤에도 DB에는 남는다.
						     플로우에 속한 스텝의 다음 목적지는 **플로우 순서**(아직 안 끝낸 스텝 중 가장 앞)가 정하므로
						     서버 StepAdvancer는 이 값을 읽자마자 버린다 → 안 쓰이는 값이니 지우게만 한다. -->
						{#if block.completion.next}
							<div class="legacy-note">
								<div class="legacy-note-head">
									예전에 지정한 <b>‘다음 스텝’</b>(<b>{blockLabel(block.completion.next)}</b>)이 남아 있어요 —
									다음 목적지는 플로우 순서가 정하므로 <b>쓰이지 않습니다</b>.
								</div>
								<div class="legacy-note-row">
									<button
										type="button"
										class="mini del"
										on:click={() => { block.completion.next = null; markDirty(block); }}
									>지우기</button>
								</div>
							</div>
						{/if}
						<!-- 저작 점검: 규칙으로 잡히는 것(즉시) + LLM이 보는 것(버튼) — 완료 유형 공통 -->
						{#if lintMap.get(block)?.length}
							<div class="step-lint">
								{#each lintMap.get(block) as r}
									<div class="lint-row" class:err={r.level === "error"}>
										<span class="lint-icon">{r.level === "error" ? "✕" : "⚠"}</span>
										<div>
											<div class="lint-msg">{r.message}</div>
											<div class="lint-why">{r.why}</div>
										</div>
									</div>
								{/each}
							</div>
						{/if}
						<div class="review-bar">
							<button
								type="button"
								class="mini"
								disabled={reviewLoading}
								on:click={() => runReview(block)}
								>{reviewLoading && reviewBlock === block
									? "보는 중…"
									: "🤖 목표 점검"}</button
							>
							<span class="review-note">유도 방식·유도문형에 목표가 맞는지 봐줘요</span>
						</div>
						{#if reviewBlock === block && reviewResult}
							<div class="step-lint">
								{#each reviewResult.findings as f}
									<div class="lint-row">
										<span class="lint-icon">🤖</span>
										<div><div class="lint-msg">{f.message}</div></div>
									</div>
								{:else}
									<div class="lint-row">
										<span class="lint-icon">✓</span>
										<div><div class="lint-msg">고칠 만한 곳을 찾지 못했어요.</div></div>
									</div>
								{/each}
							</div>
						{/if}
					{/if}
				</div>
				{/if}

				<div class="block-actions">
					<button
						class="save"
						class:has-changes={block._dirty}
						disabled={!block._dirty}
						on:click={() => saveBlock(block)}
						>{block.id ? "저장" : "생성"}</button
					>
					{#if viewMode !== "flows"}
						<button class="del" on:click={() => deleteBlock(block)}
							>삭제</button
						>
					{/if}
				</div>
			</div>
		{/each}

		{#if isTestChat}
			<!-- 테스트 챗에서 만들 수 있는 건 둘뿐이다. 이미 있으면 버튼을 감춰
			     "두 개짜리 챗"이라는 게 화면만 봐도 드러나게 한다.
			     ⚠️ 이 분기가 맨 앞이어야 한다 — viewMode 가 flows 인 채로 용도를 바꾸면
			        아래 플로우 분기에 먼저 걸려 테스트 화면이 안 뜬다. -->
			{#if !probeBlock}
				<button class="add-block" on:click={addProbeBlock}>+ 판정 기준 만들기</button>
			{/if}
			{#if !doneBlock}
				<button class="add-block" on:click={addDoneBlock}>+ 완료 화면 만들기</button>
			{/if}
		{:else if viewMode === "flows"}
			{#if currentFlow}
				<div class="add-step-bar">
					<Select
						class="tw-flex-1"
						bind:value={pickStepForFlow}
						on:change={() => {
							if (!pickStepForFlow) return;
							addExistingStepToFlow(pickStepForFlow);
							pickStepForFlow = "";
						}}
					>
						<option value="">기존 스텝 블록 담기…</option>
						{#each stepsNotInFlow as sb}
							<option value={sb.id}>{blockLabel(sb.id)}</option>
						{/each}
					</Select>
					<button class="add-block flow-add-step" on:click={createStepInFlow}
						>+ 새 스텝</button
					>
				</div>
			{/if}
		{:else if viewMode === "blocks"}
			<button class="add-block" on:click={addBlock}>+ 블록 추가</button>
		{/if}
	</div>

	<ImagePicker
		bind:open={pickerOpen}
		chatId={chat.chatId}
		contextFolder={chatAssetFolder(chat)}
		mode={pickerMode}
		currentAssetId={pickerCurrent}
		existingAssetIds={pickerExisting}
		on:select={onPickerSelect}
		on:add={onPickerAdd}
		on:close={onPickerClose}
	/>
{/if}

<style>
.block-tab {
		padding: 10px;
	}
/* ── ContentsBlock과 동일한 섹션 제목 체계 ── */
.tab-text {
		color: hsl(var(--sb-foreground));
		font-size: 20px;
		font-weight: 700;
	}
.tab-text.sub2 {
		font-size: 12px;
		padding-left: 4px;
		margin-bottom: 10px;
		display: flex;
		align-items: center;
		gap: 8px;
	}
.tab-text.sub {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
.tab-text.sub.field-hint {
		font-weight: 400;
		padding-left: 4px;
	}
.entry-hint {
		font-size: 13px;
		line-height: 1.65;
		font-weight: 500;
		color: hsl(var(--sb-muted-foreground));
		background: hsl(var(--sb-muted));
		border-radius: 8px;
		padding: 10px 14px;
		margin: 10px 0 14px;
	}
.entry-hint b {
		color: hsl(var(--sb-foreground));
	}
/* ── 폼 컨트롤 — lib/ui의 shadcn Input/Select 규격에 맞춤(h-10 · text-sm · border-input · ring focus) ── */
.text-field {
		width: 100%;
		padding: 8px 12px;
		border: 1px solid hsl(var(--sb-input));
		border-radius: calc(var(--sb-radius) - 2px);
		font-size: 14px;
		line-height: 1.5;
		font-family: inherit;
		color: hsl(var(--sb-foreground));
		background: hsl(var(--sb-background));
		box-sizing: border-box;
		transition:
			border-color 0.15s ease,
			box-shadow 0.15s ease;
	}
/* 한 줄 입력만 고정 높이(h-10). textarea는 rows가 정한다.
	   셀렉트는 lib/ui의 <Select> 컴포넌트가 같은 규격(h-10)을 갖는다. */
input.text-field {
		height: 40px;
	}
.text-field::placeholder {
		color: hsl(var(--sb-muted-foreground));
	}
.text-field:focus {
		outline: none;
		border-color: hsl(var(--sb-ring));
		box-shadow: 0 0 0 2px hsl(var(--sb-ring) / 0.35);
	}
.text-field:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
.block-card {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 12px;
		padding: 20px;
		margin-bottom: 20px;
		background: hsl(var(--sb-card));
		box-shadow: 0 1px 3px hsl(240 6% 10% / 0.06);
	}
/* 편집 중/선택(블록 클릭 또는 필드 포커스): primary 외곽선 + 링으로 "지금 이 블록" 표시 */
.block-card:focus-within {
		border-color: hsl(var(--sb-primary) / 0.6);
		box-shadow: 0 0 0 2px hsl(var(--sb-primary) / 0.18);
		outline: none;
	}
/* 변경됨(미저장): 진한 primary 테두리 + 두꺼운 링으로 강조 */
.block-card.dirty {
		border-color: hsl(var(--sb-primary));
		box-shadow: 0 0 0 3px hsl(var(--sb-primary) / 0.25);
	}
.block-head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 16px;
	}
.block-num {
		flex: none;
		font-size: 12px;
		font-weight: 700;
		color: hsl(var(--sb-primary-foreground));
		background: hsl(var(--sb-primary));
		border-radius: 6px;
		padding: 2px 7px;
	}
.head-spacer {
		flex: 1;
	}
.entry-btn {
		flex: none;
		font-size: 12px;
		padding: 4px 10px;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-muted-foreground));
		white-space: nowrap;
	}
.entry-btn.active {
		border-color: hsl(var(--sb-primary));
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
.entry-btn:disabled {
		opacity: 0.4;
		cursor: default;
	}
.block-name {
		font-size: 14px;
		font-weight: 700;
		color: hsl(var(--sb-primary));
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
	}
.block-row {
		margin-bottom: 14px;
		display: flex;
		flex-direction: column;
		gap: 7px;
	}
.block-row.inline {
		flex-direction: row;
		align-items: center;
		gap: 8px;
	}
/* 폭이 모자라면 크러시 대신 다음 줄로 줄바꿈(라벨 가독성 보장) */
.btn-row {
		flex-wrap: wrap;
	}
/* 라벨/질문 입력은 늘어나되 최소 폭(≈90px)은 지켜, 옆 필드가 길어도 안 사라지게 */
.btn-row input.text-field {
		flex: 1 1 90px;
		min-width: 90px;
	}
/* 버튼 한 개 = 편집 행 + (navigate 미완성 시) 경고줄. 기존 .block-row 하단 여백은 래퍼로 이관 */
.btn-item {
		margin-bottom: 14px;
	}
.btn-item .btn-row {
		margin-bottom: 0;
	}
/* 대상 미선택 navigate: 저장에서 빠진다는 걸 붉은 테두리로 알림 */
.btn-warn {
		font-size: 11px;
		font-weight: 500;
		color: hsl(var(--sb-destructive));
		padding: 4px 2px 0;
	}
.block-sub {
		margin: 20px 0;
		padding-top: 18px;
		border-top: 1px dashed hsl(var(--sb-border));
	}
/* 유도 목표(스텝, ⑨) 저작 */
.collect-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}
/* 지시문 행은 키/설명 행보다 한 칸 들여쓴다(어느 항목에 속한 값인지 눈으로 잡히게) */
.trait-guide-row {
		margin-left: 18px;
	}
/* [프리뷰에서 이 레슨부터] — 정리 후 새로 시작하는 동작이라 다른 mini 버튼과 구별되게 */
.preview-from {
		color: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary) / 0.4);
		white-space: nowrap;
	}
.preview-from:disabled {
		color: hsl(var(--sb-muted-foreground));
		border-color: hsl(var(--sb-border));
		cursor: not-allowed;
	}
/* 장면 유형 — 상황 칸 바로 위. 역할극이면 역할 칸이 옆에 붙는다(좁으면 줄바꿈) */
.scene-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 10px;
	}
.scene-role {
		flex: 1 1 200px;
		max-width: 320px;
	}
/* {{변수}} 칩 — 누르면 상황 칸에 붙는다 */
.var-chips {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px;
		margin-top: 4px;
	}
.var-chip {
		padding: 1px 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 999px;
		background: hsl(var(--sb-muted));
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: 11px;
		cursor: pointer;
	}
.var-chip-own {
		border-color: hsl(var(--sb-primary) / 0.4);
		background: hsl(var(--sb-primary) / 0.08);
	}
.variant-box {
		margin-bottom: 8px;
		padding: 6px 8px;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
	}
.variant-name {
		flex: 0 0 110px;
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	}
.variant-by {
		flex: 0 0 auto;
	}
.variant-val {
		flex: 0 0 64px;
		font-size: 12px;
	}
/* 이 카드가 지금 무엇인지(자유 블록 / 스텝) — shadcn Badge 톤의 작은 칩 */
.block-kind {
		flex: 0 0 auto;
		font-size: 11px;
		font-weight: 600;
		line-height: 1.4;
		padding: 2px 8px;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
	}
.block-kind.step {
		border-color: hsl(var(--sb-primary) / 0.35);
		background: hsl(var(--sb-primary) / 0.1);
		color: hsl(var(--sb-primary));
	}
/* 화면에서 사라졌지만 DB에 남아 있는 옛 값 — 꺼내 보여주는 상자 */
.legacy-note {
		margin-top: 6px;
		padding: 8px 10px;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.5);
	}
.legacy-note-head {
		font-size: 11px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		margin-bottom: 6px;
	}
.legacy-note-body {
		opacity: 0.75;
	}
.legacy-note-row {
		display: flex;
		gap: 6px;
		margin-top: 6px;
	}
/* 평가 유닛을 블록 카드로 편집 중일 때의 헤더 */
/* 필드 바로 위 한 줄 안내 — "이 칸이 실제로 무엇에 쓰이는지"를 라벨이 못 담을 때. */
/* 선택 입력 표시 — 라벨 옆 작은 칩 */
.opt-tag {
		margin-left: 4px;
		font-size: 10px;
		font-weight: 500;
		padding: 1px 6px;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
	}
/* 안내·점검 노트 공통 표면(shadcn muted). step-hint / step-lint / flow-lint가 같은 규격을 쓴다. */
.step-hint,
	.step-lint,
	.flow-lint {
		font-size: 12px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		background: hsl(var(--sb-muted) / 0.6);
		border: 1px solid hsl(var(--sb-border));
		border-radius: calc(var(--sb-radius) - 2px);
		padding: 10px 12px;
	}
.step-hint {
		margin-bottom: 10px;
	}
.step-hint b,
	.flow-lint b {
		color: hsl(var(--sb-foreground));
		font-weight: 500;
	}
.step-opts {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-top: 10px;
	}
.step-opt {
		display: flex;
		flex-direction: column;
		gap: 4px;
		font-size: 12px;
		font-weight: 500;
		color: hsl(var(--sb-muted-foreground));
	}
.step-num {
		width: 72px;
	}
/* 저작 순서 안내 — 유도 방식을 먼저 고르게 한다(목표 문구가 방식에 따라 달라지므로) */
.step-order-hint {
		font-size: 12px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		padding: 2px 2px 10px;
	}
.step-order-hint b {
		color: hsl(var(--sb-foreground));
		font-weight: 500;
	}
/* 섹션 라벨(shadcn Label 톤) */
.step-objs-head {
		font-size: 13px;
		font-weight: 500;
		color: hsl(var(--sb-foreground));
		margin: 14px 0 8px;
	}
.opt-sub {
		font-size: 12px;
		font-weight: 400;
		color: hsl(var(--sb-muted-foreground));
	}
.review-bar {
		display: flex;
		gap: 10px;
		align-items: center;
		margin-top: 12px;
	}
.review-note {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
/* 스텝 저작 즉시 점검(stepLint) — 저장을 막지 않고 어긋난 곳만 알려준다 */
.step-lint {
		display: flex;
		flex-direction: column;
		gap: 10px;
		margin-top: 12px;
	}
.lint-row {
		display: flex;
		gap: 8px;
		align-items: flex-start;
	}
.lint-icon {
		flex: none;
		width: 14px;
		text-align: center;
		color: hsl(var(--sb-muted-foreground));
	}
.lint-row.err .lint-icon {
		color: hsl(var(--sb-destructive));
	}
.lint-msg {
		color: hsl(var(--sb-foreground));
		font-weight: 500;
	}
.lint-row.err .lint-msg {
		color: hsl(var(--sb-destructive));
	}
.lint-why {
		color: hsl(var(--sb-muted-foreground));
		margin-top: 2px;
	}
/* 플로우(스텝 커리큘럼) 저작 */
.view-seg {
		display: inline-flex;
		gap: 2px;
		padding: 3px;
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.6);
		margin-bottom: 14px;
	}
/* 챗 기본값 요약 줄 / 테스트 챗 안내 — 설정 자체는 [대화 설정] 탭(ConversationSettingsContext) */
.settings-bar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px 14px;
		margin: 0 0 16px;
		padding: 8px 12px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.35);
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
.settings-bar b {
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
.settings-bar .sb-label {
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
.settings-bar button.mini {
		margin-left: auto;
	}
/* 다른 탭으로 보내는 글 속 링크 */
button.link-btn {
		padding: 0;
		border: 0;
		background: none;
		font: inherit;
		font-weight: 600;
		color: hsl(var(--sb-primary));
		text-decoration: underline;
		text-underline-offset: 2px;
		cursor: pointer;
	}
/* 스텝의 쓰는 말·학습자 말 받아 주기 덮어쓰기(완료 유형 아래). 고른 게 있으면 펼쳐 둔다 */
.step-reply {
		flex-basis: 100%;
		margin-top: 6px;
	}
.step-reply > summary {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		cursor: pointer;
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
.step-reply {
		padding: 8px 10px;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
	}
.view-seg button {
		border: 0;
		background: transparent;
		padding: 5px 14px;
		border-radius: 6px;
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
		cursor: pointer;
	}
.view-seg button.active {
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-foreground));
		box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
	}
.flow-card {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 10px;
		padding: 14px;
		margin-bottom: 12px;
		background: hsl(var(--sb-muted) / 0.3);
	}
.flow-title {
		font-weight: 600;
		margin-bottom: 10px;
	}
.flow-steps {
		display: flex;
		flex-direction: column;
		gap: 6px;
		margin-bottom: 8px;
	}
.flow-step {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 6px 8px;
		border-radius: 8px;
		background: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
	}
.flow-step-no {
		flex: 0 0 auto;
		width: 20px;
		height: 20px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 999px;
		background: hsl(var(--sb-primary) / 0.12);
		color: hsl(var(--sb-primary));
		font-size: 11px;
		font-weight: 700;
	}
.flow-step-label {
		flex: 1 1 auto;
		font-size: 12px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
.flow-empty {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		padding: 6px 2px;
	}
/* 레슨 계층 점검 요약(stepLint) — 표면 규격은 .step-hint와 공유 */
.flow-lint {
		margin-bottom: 8px;
	}
.flow-lint.err {
		color: hsl(var(--sb-destructive));
	}
.flow-tabs {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-bottom: 12px;
	}
.flow-tabs button {
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-card));
		padding: 5px 12px;
		border-radius: 999px;
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
		cursor: pointer;
	}
.flow-tabs button.active {
		background: hsl(var(--sb-primary) / 0.12);
		border-color: hsl(var(--sb-primary) / 0.4);
		color: hsl(var(--sb-primary));
	}
.flow-tabs .flow-tab-add {
		border-style: dashed;
		background: transparent;
	}
.flow-head {
		display: flex;
		gap: 8px;
		align-items: center;
		margin-bottom: 14px;
	}
.flow-head .flow-title {
		flex: 1 1 auto;
		margin-bottom: 0;
	}
/* 아래 .block-card에 붙는 머리말. 음수 마진으로 카드에 바짝 붙여 놨었는데,
	   버튼(.mini)이 shadcn 톤으로 커지면서 카드 위로 겹쳤다 → 코스 뷰 변형과 같은 8px 간격으로 통일. */
.flow-step-head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 18px 0 8px;
		padding: 0 2px;
	}
.flow-step-title {
		font-size: 13px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
	}
.block-card.in-flow {
		border-left: 3px solid hsl(var(--sb-primary) / 0.5);
	}
.add-step-bar {
		display: flex;
		gap: 8px;
		align-items: center;
		margin-top: 12px;
	}
.flow-add-step {
		width: auto;
		white-space: nowrap;
	}
/* 테스트 챗의 두 카드를 "①진행 → ②마무리" 한 줄기로 보이게 한다 */
.test-step-head {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin: 14px 0 0;
	}
.test-step-no {
		flex: 0 0 auto;
		width: 22px;
		height: 22px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 999px;
		background: hsl(var(--sb-primary) / 0.12);
		color: hsl(var(--sb-primary));
		font-size: 12px;
		font-weight: 700;
	}
.test-step-head.unused .test-step-no {
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
	}
.test-step-text {
		display: flex;
		flex-direction: column;
		line-height: 1.4;
	}
/* 되돌리는 문. 머리글 오른쪽 끝에 둬서 "이 블록을 어떻게 할지"가 설명 바로 옆에서 끝나게 한다 */
.test-step-promote {
		margin-left: auto;
		flex: 0 0 auto;
		align-self: center;
		padding: 4px 10px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		background: hsl(var(--sb-background));
		color: hsl(var(--sb-foreground));
		font-size: 12px;
		white-space: nowrap;
		cursor: pointer;
	}
.test-step-promote:hover:not(:disabled) {
		background: hsl(var(--sb-primary) / 0.08);
		border-color: hsl(var(--sb-primary) / 0.4);
		color: hsl(var(--sb-primary));
	}
.test-step-promote:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
.test-step-text b {
		font-size: 13px;
	}
.test-step-sub {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
/* 머리와 카드를 붙여 한 덩어리로 읽히게(카드 사이 기본 여백을 지운다) */
.block-card.in-test {
		margin-top: 6px;
		border-left: 2px solid hsl(var(--sb-primary) / 0.25);
	}

/* 사후 점검이 가져다 쓰는 사전 테스트 문항 — 읽기 전용 */
.linked-q {
	font-size: 13px;
	line-height: 1.5;
}
/* 판정 수집(probe) 저작 — 밴드 안에 문항이 들여쓰기로 묶여 보이게 */
.probe-band {
		border: 1px solid hsl(var(--sb-border));
		border-radius: calc(var(--sb-radius) - 2px);
		padding: 8px 10px;
		margin: 6px 0;
	}
.probe-q {
		margin-left: 14px;
	}
.probe-num {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
.probe-hint {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		margin: 2px 0 6px;
	}

/* 완료 유형(선택)·평가 루브릭 저작 */
.step-opt.step-type {
		margin: 10px 0 2px;
	}
/* [이 스텝의 상황] — 주제 칸과 같은 block-row 안에 놓인 여러 줄 입력.
   .text-field가 폭/테두리를 주므로 여기선 세로 크기와 리사이즈만 정한다. */
.situation-field {
		resize: vertical;
		min-height: 72px;
	}
.step-opt.step-rubric {
		width: 100%;
	}
.step-opt.step-rubric textarea {
		width: 100%;
		resize: vertical;
		min-height: 72px;
		font: inherit;
	}
/* 코스(레슨 커리큘럼) 저작 — 레슨 카드에 그 레슨의 스텝 요약을 보여줌 */
.course-lesson {
		border: 1px solid hsl(var(--sb-border));
		border-left: 3px solid hsl(var(--sb-primary) / 0.5);
		border-radius: 10px;
		padding: 10px 12px 12px;
		margin-bottom: 10px;
		background: hsl(var(--sb-muted) / 0.3);
	}
.course-lesson .flow-step-head {
		margin: 2px 0 8px;
	}
.course-lesson-steps {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}
.course-step-chip {
		font-size: 12px;
		padding: 3px 9px;
		border-radius: 999px;
		background: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
		color: hsl(var(--sb-foreground));
	}
.course-step-empty {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		font-style: italic;
	}
/* 코스 유닛(레슨|평가) — 평가 유닛은 코스에서 직접 저작(루브릭·통과점수·범위) */
.course-unit {
		border: 1px solid hsl(var(--sb-border));
		border-left: 3px solid hsl(var(--sb-primary) / 0.5);
		border-radius: 10px;
		padding: 10px 12px 12px;
		margin-bottom: 10px;
		background: hsl(var(--sb-muted) / 0.3);
	}
.course-unit.eval {
		border-left-color: hsl(var(--sb-destructive) / 0.6);
		background: hsl(var(--sb-destructive) / 0.04);
	}
.course-unit .flow-step-head {
		margin: 2px 0 8px;
	}
.unit-kind {
		flex: 0 0 auto;
		font-size: 11px;
		font-weight: 700;
		padding: 1px 7px;
		border-radius: 999px;
		background: hsl(var(--sb-primary) / 0.12);
		color: hsl(var(--sb-primary));
	}
.unit-kind.eval {
		background: hsl(var(--sb-destructive) / 0.12);
		color: hsl(var(--sb-destructive));
	}
.eval-editor {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
.eval-note {
		font-size: 11px;
		line-height: 1.5;
		color: hsl(var(--sb-muted-foreground));
		background: hsl(var(--sb-muted) / 0.5);
		border-radius: 6px;
		padding: 6px 8px;
	}
.eval-note b {
		color: hsl(var(--sb-foreground));
	}
.eval-hint {
		font-size: 12px;
		line-height: 1.5;
		color: hsl(var(--sb-muted-foreground));
	}
.eval-hint b {
		color: hsl(var(--sb-foreground));
	}
.eval-field {
		display: flex;
		flex-direction: column;
		gap: 4px;
		font-size: 12px;
		font-weight: 500;
		color: hsl(var(--sb-muted-foreground));
	}
.eval-field textarea {
		width: 100%;
		resize: vertical;
		min-height: 56px;
		font: inherit;
	}
.eval-row {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		gap: 12px;
	}
/* 입력폼(form) 콘텐츠 에디터 */
/* 지도 콘텐츠 */
/* 현재 선택된 이미지 미리보기 + 변경/제거 (assetId → assetUrl로 resolve) */
/* 표시 방식 아이콘 선택 — 아이콘 자체가 레이아웃(한 장/여러 장/걸치기/격자)을 나타냄 */
button {
		cursor: pointer;
		border: none;
		border-radius: 6px;
		padding: 6px 12px;
		font-size: 13px;
	}
button:disabled {
		opacity: 0.4;
		cursor: default;
	}
/* shadcn secondary 버튼 톤(작은 사이즈) */
button.mini {
		padding: 4px 10px;
		font-size: 12px;
		font-weight: 500;
		border: 1px solid hsl(var(--sb-border));
		border-radius: calc(var(--sb-radius) - 4px);
		background: hsl(var(--sb-secondary));
		color: hsl(var(--sb-secondary-foreground));
		transition: background-color 0.15s ease;
	}
button.mini:hover:not(:disabled) {
		background: hsl(var(--sb-accent));
	}
/* 추가 = 점선 아웃라인(비어 있는 자리를 채우는 동작) */
button.mini.add {
		background: transparent;
		border-style: dashed;
		color: hsl(var(--sb-primary));
	}
button.mini.add:hover:not(:disabled) {
		background: hsl(var(--sb-primary) / 0.08);
	}
/* [알아낼 것] 토글 — 고르면 primary 톤 */
.find-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px;
		margin: -2px 0 8px;
	}
button.mini.find.on {
		background: hsl(var(--sb-primary) / 0.12);
		border-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary));
	}
.fact-key {
		flex: 0 0 9rem;
	}
/* 목표 짧은 이름 — [짧은 이름] 글자 옆에 남은 폭을 채운다(통째 한 줄을 먹으면 글자와 칸이 갈라진다) */
.find-row .obj-label {
		flex: 1 1 12rem;
		min-width: 0;
	}
/* 삭제 = ghost destructive */
button.mini.del {
		background: transparent;
		border-color: transparent;
		color: hsl(var(--sb-destructive));
	}
button.mini.del:hover:not(:disabled) {
		background: hsl(var(--sb-destructive) / 0.12);
	}
.block-actions {
		display: flex;
		gap: 8px;
		margin-top: 20px;
		padding-top: 16px;
		border-top: 1px solid hsl(var(--sb-border));
	}
.block-actions button {
		padding: 10px 18px;
		border-radius: 8px;
		font-weight: 600;
	}
/* 기본(변경 없음): 중립 회색 — 누를 게 없음을 표현 */
button.save {
		background: hsl(var(--sb-secondary));
		color: hsl(var(--sb-muted-foreground));
	}
/* 변경 있음: primary로 켜져 "저장하세요" 신호 */
button.save.has-changes {
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
button.del {
		background: hsl(var(--sb-destructive) / 0.15);
		color: hsl(var(--sb-destructive));
	}
button.add-block {
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
		width: 100%;
		padding: 10px;
		border-radius: 8px;
	}
</style>
